import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { combineLatest, EMPTY, forkJoin, from, mergeMap, Observable, of, throwError } from "rxjs";
import { retryWithBackoff } from "../../libs/rxjs/retryWithBackoff";
import { catchError, delay, finalize, map, switchMap, take, timeout } from "rxjs/operators";
import {
    ADMINISTRATOR,
    appData,
    compareIds,
    concatIdBusinessId,
    DEFAULT_PROTOCOL,
    DEFAULT_SERVER, DEV_TEST_PORT,
    getCredentialsHeader,
    getCredentialsHeaderImage,
    getDefaultStationStats,
    isBase64,
    isGalaxy,
    isHostDev,
    isHostDevOrLocal,
    isHostLocal,
    isNullOrUndef,
    isNullOrUndefOrEmpty,
    jsonToBase64,
    NONE_VALUE,
    preProcessSceneTree,
    SERVERS,
    SUPPORTED_FILE_TYPES,
} from "../common";
import {
    BusinessId,
    EnumStudioType,
    IAccountData,
    IAccountReplyV2,
    IAccountRequest,
    IAnnualPayData,
    IAppSettingsWrap,
    IAppStatic,
    IBusinessDomainCollection,
    IBusinessUsage,
    ICart,
    ICartCreate,
    ICartProduct,
    ICartReply,
    ICartRequest,
    ICatalogItemCategories,
    ICatalogItemInfos,
    ICatalogItemResources,
    ICatalogItems,
    ICategoryValues,
    ICRUDRecord,
    ICRUDRecordRead,
    ICRUDRecordReadReply,
    ICRUDRecordReply,
    Id,
    IDynawebsCustomer,
    IDynawebsServerReply,
    IEmailSend,
    IEventBlock,
    IEventParam,
    IEventScope,
    IIot,
    IFeedFieldsResponse,
    IFeedMeAnyResponse,
    IGlobalDataRequest,
    IGlobalSettings,
    IMusicChannel,
    IMusicChannelSong,
    IOrderDetails,
    IOrderForm,
    IPaintSource,
    IPlayerProps,
    IPreferencesBusiness,
    IPreloadResellerInfo,
    IResource,
    IResourceSharedItem,
    ISceneTemplateImport,
    ISchedule,
    IScheduleEvent,
    ISharedResourceLibrary,
    ISocialReply,
    ISocialToken,
    IStation,
    IStationData,
    IStationGeoIp,
    IStationLogRequest,
    IStationStatsV1,
    IStationStatsV1V2,
    ITransitionPool,
    ITransitionPoolItem,
    IUser,
    IUserSaveDetailsReply,
    IUserSync,
    IUserUpgradeReply,
} from "../interfaces";
import { StoreSelectorsService } from "./store-selectors.service";
import { actionAppSettings, actionStationV1V2Update, actionTwoFactorCheckAccess } from "../store/actions/user.actions";
import { ToastrService } from "ngx-toastr";
import { DebugService } from "./debug.service";
import { SocialUser } from "@abacritt/angularx-social-login";
import { ISocialServerReply } from "../interfaces/social-interface";
import { TranslocoService } from "@ngneat/transloco";
import { IAccountDetails } from "../interfaces/enterprise-account-interface";
import { Compbaser } from "../components/compbaser";
import { LocalStoreService } from "./local-store.service";
// type-only: the account/scene template tables (~250KB) are only needed by the getstarted template
// list, so they are fetched on demand rather than riding in the shell every page downloads
import type { ISceneImportTemplate } from "../../assets/accountTemplates/scene-template-constants";

const loadAccountTemplates = () => import("../../assets/accountTemplates/acounts-constants");
const loadSceneTemplates = () => import("../../assets/accountTemplates/scene-template-constants");
import { SelectionModel } from "@angular/cdk/collections";
import io, { Socket } from "socket.io-client";
import { IChatServerReply, IChatStats } from "../interfaces/chat-interface";
import {
    ICreateTicketRequest,
    ICreateTicketResponse,
    IGetTicketsFilter,
    IGetTicketsResponse,
    IGetTicketResponse,
    IUpdateTicketRequest,
    IUpdateTicketResponse,
    IAddTicketMessageRequest,
    IAddTicketMessageResponse,
    ICloseTicketRequest,
    ICloseTicketResponse,
    IGetTicketStatsResponse,
    IGetCustomerTicketRequest,
    IGetCustomerTicketResponse,
    IAddCustomerTicketMessageRequest,
    IAddCustomerTicketMessageResponse,
    IGetCannedResponsesResponse,
    IAddCannedResponseRequest,
    IAddCannedResponseResponse,
    IUpdateCannedResponseRequest,
    IUpdateCannedResponseResponse,
    IDeleteCannedResponseRequest,
    IDeleteCannedResponseResponse,
} from "../interfaces/tickets-interfaces";
import { Lib } from "../../libs/Lib";
import * as xml2js from "xml2js";
import * as _ from "lodash";
import { Action } from "@ngrx/store";
import { FintechService } from "./fintech.service";

/**
 * XML Parser Configuration Notes
 *
 * This service uses xml2js for XML parsing: https://github.com/Leonidas-from-XIV/node-xml2js
 * Alternative parser available at: https://github.com/michaelkourlas/node-js2xmlparser
 *
 * To make xml2js work in Angular, the following dependencies are required:
 * - "sax": "^1.2.4"
 * - "stream": "0.0.2"
 * - "timers": "^0.1.1"
 *
 * And tsconfig.json must include these path mappings:
 * "paths": {
 *   "sax": ["node_modules/sax"],
 *   "timers": ["node_modules/timers"],
 *   "stream": ["node_modules/stream"]
 * }
 *
 * Browser-specific version (not used): https://github.com/mboughaba/browser-xml2js
 */

declare const $: any;

/**
 * HTTP options configuration for JSON requests
 */
const httpOptionsJson = {
    headers: new HttpHeaders({ "Content-Type": "application/json" }),
    responseType: "json" as "json",
};

/**
 * Core User Service - Central Hub for SignStudio Application Data Management
 *
 * @description
 * UserService is the primary service responsible for managing all user-related operations,
 * business data synchronization, and API communications in SignStudio. This service acts as
 * the main data orchestration layer between the Angular frontend and the mediaSERVER/mediaCLOUD backend.
 *
 * After successful user authentication, this service serializes and populates the entire
 * application state with user-specific data including scenes, resources, stations, catalogs,
 * schedules, and more.
 *
 * @responsibilities
 * - **Authentication & User Management**: Social login, account creation, password management, 2FA
 * - **Business Data Loading**: Scenes, resources, stations, catalogs, music channels, transition pools
 * - **CRUD Operations**: Generic create, read, update, delete operations for all data entities
 * - **Station Management**: Station commands, statistics, geo-location, live monitoring
 * - **Resource Management**: File uploads, shared resources, proxy requests, Pexels integration
 * - **AI Services**: Image generation (DALL-E, Google Veo), video generation (Runway, Veo), text-to-speech
 * - **Shopping Cart & Billing**: Product management, order processing, payment integration (PayPal, Stripe)
 * - **Enterprise Features**: DynaWebs integration, reseller management, business usage tracking
 * - **Social Integration**: Social media posting, profile management (Facebook, Google, LDAP)
 * - **Templates & Samples**: Scene template management, account template provisioning
 * - **Chat & Support**: Live chat integration, support statistics, archive management
 * - **Analytics & Reporting**: Account statistics, station logs, conversion reports
 *
 * @architecture
 * This service integrates with:
 * - **NgRx Store**: For centralized state management via StoreSelectorsService
 * - **HTTP Client**: For all REST API communications with retry logic and error handling
 * - **Socket.io**: For real-time chat and station monitoring
 * - **xml2js**: For XML parsing of scene data and business configurations
 * - **RxJS**: Extensive use of observables, operators, and reactive patterns
 *
 * @dataFlow
 * 1. User logs in → `getBusinessUserInfoV3()` fetches account details
 * 2. Account data triggers → `getUserScenesData()` loads all business entities
 * 3. Data is parsed and normalized → Dispatched to NgRx store
 * 4. UI components subscribe to store selectors → Reactive updates
 * 5. User actions trigger CRUD operations → `crudRecord()` handles persistence
 * 6. Real-time updates via WebSocket → Station stats and chat messages
 *
 * @errorHandling
 * - All HTTP requests include retry logic with exponential backoff
 * - Toast notifications for user-facing errors
 * - Console logging for debugging
 * - Email notifications to admin for critical errors
 *
 * @performance
 * - Scene data is processed in chunks to avoid blocking
 * - Base64 encoding/decoding for large XML payloads
 * - Caching strategies for templates and static resources
 * - Lazy loading of station statistics and geo-location data
 *
 * @security
 * - Credentials encrypted with Base64 encoding
 * - Authorization headers for all authenticated requests
 * - 2FA support with TOTP tokens
 * - Social login token verification
 *
 * @extends Compbaser - Base component class with lifecycle management and subscription cleanup
 *
 * @see {@link IUser} - User interface definition
 * @see {@link IAccountData} - Business data structure
 * @see {@link ICRUDRecord} - CRUD operation interface
 * @see {@link StoreSelectorsService} - NgRx store integration
 *
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private userService: UserService) {}
 *
 * // Load user scenes and resources
 * this.userService.getUserScenesData(businessId, changelistId)
 *   .subscribe(accountData => {
 *     console.log('Loaded scenes:', accountData.scenes);
 *     console.log('Loaded resources:', accountData.resources);
 *   });
 *
 * // Create a new scene
 * const crudRecord: ICRUDRecord = {
 *   table: 'player_data',
 *   primary: 'player_data_id',
 *   command: 'add',
 *   body: { player_data_value: sceneXml }
 * };
 * this.userService.crudRecord(crudRecord).subscribe(result => {
 *   console.log('Scene created with ID:', result.id);
 * });
 * ```
 */
@Injectable()
export class UserService extends Compbaser {
    /** Currently authenticated user object containing credentials and business info */
    private m_user: IUser;

    /** Application-wide static settings including server URLs and feature flags */
    private appSettings: IAppStatic;

    /** User access mask array for permission-based resource filtering */
    private userMask: Array<string>;

    /** Flag indicating if user has full features access vs. lite mode */
    private isFullFeatures: boolean;

    /** Studio type enum (SIGN_STUDIO, PRO, LITE, etc.) */
    private studioType: EnumStudioType;

    /** Core application data wrapper containing business and global settings */
    private coreAppData: IAppSettingsWrap;

    /** Socket.io instance for real-time chat functionality */
    private socketChat: Socket;

    /**
     * Initializes the UserService and sets up reactive subscriptions to store data
     *
     * @description
     * The constructor establishes the service's connection to the NgRx store and sets up
     * reactive subscriptions to keep the service synchronized with application state changes.
     * All subscriptions are managed via the `cod()` method (Component On Destroy) inherited
     * from Compbaser, ensuring proper cleanup when the service is destroyed.
     *
     * @param translocoService - Service for internationalization and translations
     * @param _http - Angular HTTP client for making API requests
     * @param localStoreService - Service for local storage operations
     * @param storeSelector - Facade service for accessing NgRx store selectors
     * @param toast - Toastr service for displaying user notifications
     * @param fintechService - Service for financial transaction operations (Stripe, etc.)
     *
     * @remarks
     * During initialization, the service:
     * 1. Dispatches app settings to the store
     * 2. Subscribes to feature flags (full features vs. lite mode)
     * 3. Subscribes to core application data
     * 4. Subscribes to user access mask for permissions
     * 5. Subscribes to current user state
     * 6. Subscribes to application settings
     *
     * All subscriptions are automatically cleaned up when the service is destroyed,
     * preventing memory leaks.
     */
    constructor(
        private translocoService: TranslocoService,
        private _http: HttpClient,
        private localStoreService: LocalStoreService,
        private storeSelector: StoreSelectorsService,
        private toast: ToastrService,
        private fintechService: FintechService,
        private debugService: DebugService) {

        super();

        // Initialize app settings in store
        this.storeSelector.dispatch(actionAppSettings({ appData }));

        // Subscribe to feature flags
        this.cod(
            this.storeSelector.isFullFeatures().subscribe((v) => {
                this.isFullFeatures = v;
            }),
        );

        // Subscribe to core application data
        this.cod(
            this.storeSelector.getCoreData()
                .subscribe((i_coreData: IAppSettingsWrap) => {
                    this.coreAppData = i_coreData;
                }),
        );

        // Subscribe to user access mask for permission filtering
        this.cod(
            this.storeSelector.getUserAccessMask()
                .subscribe((value) => {
                    this.userMask = value;
                }),
        );

        // Subscribe to current user state
        this.cod(
            storeSelector.getUser()
                .subscribe((user: IUser) => {
                    this.m_user = user;
                    this.studioType = user.studioType;
                }),
        );

        // Subscribe to application settings
        this.cod(
            storeSelector.getAppSettings()
                .subscribe((appSettings: IAppStatic) => {
                    this.appSettings = appSettings;
                }),
        );
    }

    // ============================================================================
    // SECTION: Private Utility Methods
    // ============================================================================

    /**
     * Displays an error message to the user via toast notification and logs to console
     *
     * @param i_msg - Error message to display and log
     *
     * @private
     * @remarks
     * This is a centralized error handler used throughout the service to maintain
     * consistent error reporting to both the console (for debugging) and the user
     * (via toast notifications).
     */
    private onError(i_msg) {
        console.log("error " + i_msg);
        this.toast.error(i_msg);
    }

    // ============================================================================
    // SECTION: Shared Resources Management
    // ============================================================================

    /**
     * Retrieves shared resources library from multiple business domains
     *
     * @description
     * Fetches shared resources from multiple SignStudio business domains across different servers.
     * This allows users to access and utilize resources shared by other businesses within
     * the SignStudio ecosystem. The method performs parallel HTTP requests to all specified
     * domains and aggregates the results.
     *
     * @param businessDomains - Collection of business domain configurations organized by server domain
     *
     * @returns Observable<ISharedResourceLibrary> - Library containing all shared resources with metadata
     *
     * @remarks
     * - Performs parallel HTTP requests using forkJoin for optimal performance
     * - Parses XML responses to extract resource metadata
     * - Constructs full resource URLs using S3 cloud storage paths
     * - Filters out resources with empty tree paths
     * - Handles XML parsing errors and sends admin notifications on critical failures
     *
     * @example
     * ```typescript
     * const domains: IBusinessDomainCollection = {
     *   'galaxy.signage.me': { businesses: ['123', '456'] },
     *   'earth.signage.me': { businesses: ['789'] }
     * };
     * this.userService.getSharedResourcesLibrary(domains)
     *   .subscribe(library => {
     *     console.log('Shared resources:', library.library);
     *   });
     * ```
     */
    public getSharedResourcesLibrary(businessDomains: IBusinessDomainCollection): Observable<ISharedResourceLibrary> {
        const observables: Array<Observable<any>> = [];
        // tslint:disable-next-line:forin
        for (const source in businessDomains) {
            // const i_businesses = config[source];
            // if (source.indexOf('mars') > -1 || source.indexOf('mercury') > -1) continue;
            const url = `https://${source}/WebService/SharedDataService.asmx/GetSharedResources?i_search=`;
            observables.push(this._http.get(url, {
                headers: new HttpHeaders({
                    "Content-Type": "text/xml",
                    "Accept": "text/xml",
                }), responseType: "text" as "json",
            })
                .pipe(
                    retryWithBackoff(url, this.storeSelector, this.toast),
                    map(xml => ({ xml, source })),
                    catchError((e) => {
                        console.error("problem loading users " + e + " " + source + " " + e.toString());
                        this.onError(this.translocoService.translate("toast.sorryEncounteredProblem", {}, this.translocoService.getActiveLang()));
                        return e;
                    }),
                ));
        }
        return forkJoin(observables).pipe(
            map((val) => {
                const resourcesLibrary: Array<IResourceSharedItem> = [];
                val.forEach((config) => {
                    const xmlData = config.xml.replace(/&lt;/ig, "<").replace(/&gt;/ig, ">");
                    xml2js.parseString(xmlData, (err, result) => {
                        if (err) {
                            console.error("getBusinessDomainCollection xml conversion error");
                            this.storeSelector.sendEmail(ADMINISTRATOR, "error in painter json", this?.m_user?.email, true);
                            return this.onError(this.translocoService.translate("toast.sorryEncounteredProblem", {}, this.translocoService.getActiveLang()));
                        }
                        const sharedResources: Array<IResourceSharedItem> = result.Result.Resource;
                        sharedResources.forEach((resource) => {
                            if (resource.$.treePath === "") return;
                            resource.url = `${this.appSettings.cloudResourcesS3}/business${resource.$.businessId}/resources/${resource.$.resourceId}.${resource.$.resourceType}`;
                            resourcesLibrary.push(resource);
                        });
                    });
                });
                return {
                    version: "0",
                    library: resourcesLibrary,
                };
            }));
    }

    // testSaveDataUpdate() {
    //     const body = {player_data_value: "Update3"};
    //     this.CRUDRecord('player_data', 'player_data_id', 'update', body, 226);
    // }

    // testSaveDataDelete() {
    // this.CRUDRecord('player_data', 'player_data_id', 'delete', null, 243);
    // }

    // testSaveDataFileSelected(event) {
    //     const file: File = event.target.files[0];
    //     if (file) {
    //         const formData = new FormData();
    //         formData.append("thumbnail", file);
    //         // this.CRUDRecord('resources', 'resource_id', 'upload', formData, null);
    //     }
    // }

    // testSaveDataAdd() {
    //     const body = {
    //         player_data_value: "<Player player=\"3510\" label=\"Label1\" mimeType=\"mainPlayer\"><Metadata><Theme id=\"1000\" /></Metadata><Data><Layout rotation=\"0\" x=\"0\" y=\"0\" width=\"300\" height=\"300\" /><Background color=\"13408767\" alpha=\"1\" /><Scene defaultDuration=\"10\" scrolling=\"0\"><Layout><BasicLayout /></Layout><Players><Player player=\"3241\" label=\"item3\"><AnchorLayoutData left=\"NaN\" horizontalCenter=\"NaN\" right=\"10\" top=\"NaN\" verticalCenter=\"-10\" bottom=\"NaN\" /><Data><Layout rotation=\"0\" x=\"146\" y=\"-1.4210854715202004e-14\" width=\"154\" height=\"100\" /><Background color=\"3381759\" alpha=\"1\" /><Label><Text>Item3</Text><Font fontSize=\"36\" fontColor=\"13382400\" fontFamily=\"Bearpaw\" fontWeight=\"normal\" fontStyle=\"normal\" textDecoration=\"none\" textAlign=\"left\" autoScaling=\"0\" /></Label></Data></Player><Player player=\"3241\" label=\"item4\" autoScaling=\"1\"><AnchorLayoutData left=\"NaN\" horizontalCenter=\"NaN\" right=\"10\" top=\"NaN\" verticalCenter=\"-10\" bottom=\"NaN\" /><Data><Layout rotation=\"0\" x=\"146\" y=\"100\" width=\"154\" height=\"100\" /><Background color=\"13395660\" alpha=\"1\" /><Label><Text>Item4</Text><Font fontSize=\"36\" fontColor=\"16776960\" fontFamily=\"Bearpaw\" fontWeight=\"normal\" fontStyle=\"normal\" textDecoration=\"none\" textAlign=\"left\" autoScaling=\"0\" /></Label></Data></Player><Player player=\"3241\" label=\"item1\"><AnchorLayoutData left=\"10\" horizontalCenter=\"NaN\" right=\"NaN\" top=\"NaN\" verticalCenter=\"10\" bottom=\"NaN\" /><Data><Layout rotation=\"0\" x=\"0\" y=\"-1.4210854715202004e-14\" width=\"146\" height=\"100\" /><Background color=\"26316\" alpha=\"1\" /><Label><Text>Item1;`a2`</Text><Font fontSize=\"14\" fontColor=\"65280\" fontFamily=\"Arial\" fontWeight=\"normal\" fontStyle=\"normal\" textDecoration=\"none\" textAlign=\"left\" autoScaling=\"0\" /></Label></Data></Player><Player player=\"3241\" label=\"item2\" autoScaling=\"1\"><AnchorLayoutData left=\"10\" horizontalCenter=\"NaN\" right=\"NaN\" top=\"NaN\" verticalCenter=\"10\" bottom=\"NaN\" /><Data><Layout rotation=\"0\" x=\"0\" y=\"100\" width=\"146\" height=\"100\" /><Background color=\"65484\" alpha=\"1\" /><Label><Text>Item2</Text><Font fontSize=\"36\" fontColor=\"13395711\" fontFamily=\"Bearpaw\" fontWeight=\"normal\" fontStyle=\"normal\" textDecoration=\"none\" textAlign=\"left\" autoScaling=\"0\" /></Label></Data></Player></Players></Scene></Data></Player>"
    //     };
    // }

    // ============================================================================
    // SECTION: CRUD Operations (Create, Read, Update, Delete)
    // ============================================================================

    /**
     * Performs generic CRUD operations on database tables
     *
     * @description
     * This is the primary method for all database create, read, update, and delete operations
     * in SignStudio. It provides a unified interface for persisting changes to scenes, resources,
     * stations, catalogs, and all other database entities. The method handles authentication,
     * error handling, retry logic, and optional progress reporting for file uploads.
     *
     * @param crudRecord - CRUD operation configuration object
     * @param crudRecord.table - Database table name (e.g., 'player_data', 'resources', 'stations')
     * @param crudRecord.primary - Primary key column name (e.g., 'player_data_id', 'resource_id')
     * @param crudRecord.command - CRUD command ('add', 'update', 'delete', 'upload')
     * @param crudRecord.body - Data payload (object for add/update, FormData for upload)
     * @param crudRecord.id - Record ID (optional for add, required for update/delete)
     * @param crudRecord.studioId - Studio identifier
     * @param crudRecord.ext - File extension (for upload operations)
     * @param crudRecord.name - File name (for upload operations)
     * @param crudRecord.overrideCredentials - Optional override for user credentials
     * @param crudRecord.options - Optional configuration (noStringify, reportProgress)
     *
     * @returns Observable<ICRUDRecordReply> - Reply object containing status and new record ID
     *
     * @remarks
     * **Important Features:**
     * - Automatic retry with exponential backoff on network failures
     * - Base64 encoding of passwords for security
     * - Optional PWA player restart after scene saves (controlled by restartPlayer config)
     * - Support for file upload progress reporting
     * - Handles both JSON payloads and FormData for file uploads
     *
     * **Error Handling:**
     * - Shows user-friendly toast notifications on errors
     * - Returns error details in reply object
     * - Logs errors to console for debugging
     *
     * **Performance:**
     * - Uses HTTP POST for all operations
     * - Supports progress reporting for large file uploads
     * - Optional JSON.stringify bypass via noStringify option
     *
     * @example
     * ```typescript
     * // Create a new scene
     * const addScene: ICRUDRecord = {
     *   table: 'player_data',
     *   primary: 'player_data_id',
     *   command: 'add',
     *   studioId: 1,
     *   body: { player_data_value: sceneXml, label: 'My Scene' }
     * };
     * this.userService.crudRecord(addScene).subscribe(result => {
     *   if (result.status) {
     *     console.log('Scene created with ID:', result.id);
     *   }
     * });
     *
     * // Update existing resource
     * const updateResource: ICRUDRecord = {
     *   table: 'resources',
     *   primary: 'resource_id',
     *   command: 'update',
     *   id: '123',
     *   studioId: 1,
     *   body: { resource_name: 'Updated Name' }
     * };
     * this.userService.crudRecord(updateResource).subscribe(result => {
     *   console.log('Resource updated:', result.status);
     * });
     *
     * // Delete a station
     * const deleteStation: ICRUDRecord = {
     *   table: 'stations',
     *   primary: 'station_id',
     *   command: 'delete',
     *   id: '456',
     *   studioId: 1,
     *   body: {}
     * };
     * this.userService.crudRecord(deleteStation).subscribe(result => {
     *   console.log('Station deleted:', result.status);
     * });
     *
     * // Upload a file with progress tracking
     * const formData = new FormData();
     * formData.append('file', fileBlob);
     * const uploadFile: ICRUDRecord = {
     *   table: 'resources',
     *   primary: 'resource_id',
     *   command: 'upload',
     *   studioId: 1,
     *   name: 'myfile',
     *   ext: 'jpg',
     *   body: formData,
     *   options: { reportProgress: true }
     * };
     * this.userService.crudRecord(uploadFile).subscribe(event => {
     *   if (event.type === HttpEventType.UploadProgress) {
     *     const percentDone = Math.round(100 * event.loaded / event.total);
     *     console.log(`Upload ${percentDone}%`);
     *   }
     * });
     * ```
     *
     * @todo Add compression gzip support for large payloads
     * @see https://betterexplained.com/articles/how-to-optimize-your-site-with-gzip-compression/
     */
    crudRecord(crudRecord: ICRUDRecord): Observable<ICRUDRecordReply> {
        let url: string;
        let user = this?.m_user?.email;
        let password = this?.m_user?.passwordNew;
        let webApiBaseUrl = this?.m_user?.webApiBaseUrl;
        if (crudRecord.overrideCredentials) {
            user = crudRecord.overrideCredentials.user;
            password = crudRecord.overrideCredentials.password;
            webApiBaseUrl = crudRecord.overrideCredentials.webApiBaseUrl;
        }
        url = `${webApiBaseUrl}/Web/saveData.ashx?user=${user}&password=${Lib.Base64().encode(password)}&table=${crudRecord.table}&primary=${crudRecord.primary}&studioId=${crudRecord.studioId}&command=${crudRecord.command}&base64=1`;
        if (crudRecord.id != null) {
            url += crudRecord.command === "add" ? `&id=${crudRecord.id}` : `&ids=[${crudRecord.id}]`;
        }
        if (crudRecord.ext != null) url += `&name=${crudRecord.name}&ext=${crudRecord.ext}`;
        const restartPlayer = this.localStoreService.getStorageValConfig("restartPlayer") == "1" ? true : false;
        /** replay is only for PWA Signage Players and a scene is saved, if assigned to player, it will restart it **/
        url += `&replay=${restartPlayer}`;

        // switch (crudRecord.command) {
        //     case 'delete': {
        //         if (crudRecord.id !== null) url += `&ids=[${crudRecord.id}]`;
        //         break;
        //     }
        //     default: {
        //         if (crudRecord.id !== null) url += `&ids=[${crudRecord.id}]`;
        //         break;
        //     }
        // }

        const noStringify = crudRecord?.options?.noStringify;
        let opt = {};
        if (crudRecord?.options?.reportProgress) {
            opt = { reportProgress: true, observe: "events", headers: new HttpHeaders({ "ngsw-bypass": "" }) };
        }
        return this._http.post(url,
            noStringify ? crudRecord.body : JSON.stringify(crudRecord.body), opt)
            .pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err) => {
                    this.showSaveError(err, crudRecord.command);
                    return throwError(err);
                }),
                finalize(() => {
                }),
                switchMap((result: ICRUDRecordReply) => {
                    if (opt["reportProgress"] && result && result.hasOwnProperty("type") && result.hasOwnProperty("status") == false) {
                        return of(result);
                    }
                    if (!result?.status) {
                        // display_settings was added to branch_stations only on new servers.
                        // If a legacy server rejects it, silently retry without that field so
                        // all other station fields are still saved.
                        const errMsg = String(result?.['msg'] || result?.['message'] || result?.['error'] || '');
                        if (crudRecord.body?.display_settings !== undefined && errMsg.includes('display_settings')) {
                            const retryBody = {...crudRecord.body};
                            delete retryBody.display_settings;
                            return this.crudRecord({...crudRecord, body: retryBody});
                        }
                        this.showSaveError(result, crudRecord.command);
                        return of({ id: null, status: false } as ICRUDRecordReply);
                    }
                    return of(result);
                }),
            );
    }

    // fileUpload(crudRecord: ICRUDRecord, callback = null) {
    //     const formData = crudRecord.body;
    //     const xhr = new XMLHttpRequest();

    //     let url = `${this.m_user.webApiBaseUrl}/Web/saveData.ashx?user=${this.m_user.email}&password=${this.m_user.passwordNew}&table=${crudRecord.table}&primary=${crudRecord.primary}&studioId=${crudRecord.studioId}&command=${crudRecord.command}`;
    //     xhr.open("POST", url);

    //     xhr.upload.addEventListener("loadstart", (evt) => {
    //         console.log("upload starting...\n");
    //     }, false);

    //     xhr.upload.addEventListener("progress", (evt) => {
    //         var percentLoaded = (evt.loaded / evt.total) * 100;
    //         console.log(evt.loaded + "% loaded\n");
    //         console.log(percentLoaded + "%\n");
    //         if (callback) callback(percentLoaded.toFixed(2));
    //     }, false);

    //     xhr.upload.addEventListener("load", (evt) => {
    //         console.log("upload complete!");
    //         if (callback) callback(100);
    //     }, false);

    //     xhr.send(formData);
    // }

    /**
     * Reads cached record data from the server
     *
     * @description
     * Retrieves cached player data, resource information, or other entity details from the
     * mediaSERVER cache. This method first resolves the business domain, then fetches the
     * cached data from the appropriate server. Commonly used for loading scene templates,
     * resource metadata, and other read-only data.
     *
     * @param crudRecord - CRUD read operation configuration
     * @param crudRecord.businessId - Business ID to determine server domain
     * @param crudRecord.id - Record ID to fetch
     * @param crudRecord.changelist - Changelist ID for versioning
     * @param crudRecord.table - Database table name
     * @param crudRecord.reqFor - Request purpose identifier
     *
     * @returns Observable<ICRUDRecordReadReply> - Record data including player_data_value, resource metadata
     *
     * @remarks
     * - First performs a JSONP call to resolve the business domain
     * - Then performs a POST to load the cached data from the resolved domain
     * - Uses retry with backoff for both operations
     * - Returns null on errors to prevent app crashes
     *
     * @example
     * ```typescript
     * const readRecord: ICRUDRecordRead = {
     *   businessId: '123456',
     *   id: '789',
     *   changelist: '0',
     *   table: 'player_data',
     *   reqFor: 'sceneTemplate'
     * };
     * this.userService.crudRecordReadCall(readRecord).subscribe(data => {
     *   console.log('Scene XML:', data.player_data_value);
     * });
     * ```
     */
    crudRecordReadCall(crudRecord: ICRUDRecordRead): Observable<ICRUDRecordReadReply> {
        //   const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=GetAccountStatus&businessId=${businessId}`;
        //   return this._http.jsonp<any[]>(url, 'callback').pipe(
        const getDomainUrl = `${DEFAULT_PROTOCOL}${DEFAULT_SERVER}/WebService/getBusinessDomain.ashx?businessId=${crudRecord.businessId}`;
        return this._http.jsonp(getDomainUrl, "callback")
            .pipe(
                retryWithBackoff(getDomainUrl, this.storeSelector, this.toast),
                catchError((err) => {
                    this.showSaveError(err, "crudRecordReadCall 1");
                    return throwError(err);
                }),
                map((v: any) => {
                    return `${DEFAULT_PROTOCOL}${v.domain}/Web/loadCachedData.ashx?id=${crudRecord.id}&changelist=${crudRecord.changelist}&businessId=${crudRecord.businessId}&table=${crudRecord.table}&reqFor=${crudRecord.reqFor}`;
                }),
                mergeMap((u: any) => {
                    return this._http.post(u, {});
                }))
            .pipe(
                retryWithBackoff("loadCachedData...", this.storeSelector, this.toast),
                catchError((err) => {
                    this.showSaveError(err, "crudRecordReadCall 2");
                    return throwError(err);
                }), map((i_serveReply: any) => {
                    const record: ICRUDRecordReadReply = {
                        player_data_value: i_serveReply?.player_data_value,
                        resource_bytes_total: i_serveReply?.resource_bytes_total,
                        resource_name: i_serveReply?.resource_name,
                        resource_type: i_serveReply?.resource_type,
                    };
                    return record;
                }));
    }

    /**
     * Proxies resource requests through mediaSERVER to handle CORS and access restrictions
     *
     * @description
     * Some resources (especially from S3 or external sources) require proxying through the
     * SignStudio servers to bypass CORS restrictions or access control. This method provides
     * two proxy modes: NODE (Node.js server) and IIS (Windows server) to accommodate different
     * deployment environments.
     *
     * @param i_value - Resource value (either Base64 encoded path for NODE or resource object for IIS)
     * @param type - Proxy type: 'NODE' (Node.js server) or 'IIS' (Windows server)
     *
     * @returns Observable<any> - Proxied resource content
     *
     * @remarks
     * **NODE Proxy:**
     * - Expects Base64 encoded resource path
     * - Decodes path with atob()
     * - Routes through global service endpoint
     * - Example: `/proxyRequest/https://domain.com/path/to/resource`
     *
     * **IIS Proxy:**
     * - Expects resource object with resource_id and resource_type
     * - Constructs S3 path automatically
     * - Routes through Web/redirectRequest.ashx
     * - Example: `https://earth.signage.me/Web/redirectRequest.ashx?url=https://s3...`
     *
     * @example
     * ```typescript
     * // NODE proxy for SVG
     * const svgPath = btoa('https://example.com/image.svg');
     * this.userService.getProxyResource(svgPath, 'NODE')
     *   .subscribe(data => console.log('SVG data:', data));
     *
     * // IIS proxy for image resource
     * const resource = { resource_id: '405', resource_type: 'jpg' };
     * this.userService.getProxyResource(resource, 'IIS')
     *   .subscribe(data => console.log('Image data:', data));
     * ```
     */
    getProxyResource(i_value, type: "NODE" | "IIS"): Observable<any> {
        let url;
        switch (type) {
            case "NODE": {
                const svgPath = atob(i_value);
                url = `${this.coreAppData[1].globalService}/proxyRequest/${svgPath}`;
                break;
            }
            case "IIS": {
                url = `${this.coreAppData[0].webApiBaseUrl}/Web/redirectRequest.ashx?url=${this.coreAppData[1].cloudResourcesS3}/business${this.coreAppData[0].businessId}/resources/${i_value.resource_id}.${i_value.resource_type}`;
                break;
            }
        }
        const opt = {};
        return this._http.post(url, { url: i_value }, opt)
            .pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err) => {
                    console.error("getProxyResource error command " + err);
                    this.onError(this.translocoService.translate("toast.sorryProxyProblem", {}, this.translocoService.getActiveLang()));
                    return throwError(err);
                }),
                finalize(() => {
                }),
                map((result: any) => {
                    return result;
                }),
            );
    }

    // ============================================================================
    // SECTION: User Data Loading & Synchronization
    // ============================================================================

    /**
     * Loads complete business account data including scenes, resources, stations, and more
     *
     * @description
     * **This is the primary data loading method for SignStudio after user authentication.**
     * It fetches and deserializes all business entities from the mediaSERVER, transforming
     * raw XML/JSON data into strongly-typed TypeScript objects that populate the application state.
     * This method is called immediately after login and handles incremental updates via changelist IDs.
     *
     * **Data Entities Loaded:**
     * - Scenes (player_data) - Digital signage content with XML parsing
     * - Resources (images, videos, fonts, etc.) - With permission filtering
     * - Stations (v1 and v2) - Connected digital signage players
     * - Global Settings - Business-wide configuration parameters
     * - Preferences - User/business preference settings including 2FA
     * - Music Channels & Songs - Background music management
     * - Transition Pools & Items - Visual transition effects
     * - Catalog Items & Categories - Product catalog system
     * - Schedules & Events - Timeline scheduling system
     * - Painters - Custom SVG/canvas graphics data
     * - Event Parameters - Dynamic event configurations
     *
     * @param businessId - Business account ID
     * @param i_changelistId - Changelist ID for incremental sync (use NONE_VALUE for full load)
     *
     * @returns Observable<IAccountData> - Complete account data object
     *
     * @remarks
     * **Data Processing:**
     * - Scenes are parsed from XML to JSON using xml2js
     * - Resources are filtered by supported file types and user permissions
     * - Stations are versioned (v1 for legacy, v2 for new players)
     * - Default station stats are created for v2 stations
     * - Painters have Base64 decoded JSON data
     * - Event names are extracted from scene tree for filtering
     *
     * **Changelist Synchronization:**
     * - Full load: pass NONE_VALUE as changelistId
     * - Incremental: pass last known changelistId to get only changes
     * - Items with change_type === 3 are deleted (excluded unless full load)
     *
     * **Error Handling:**
     * - Corrupted scenes/painters are skipped with error logging
     * - Admin email notifications sent for critical errors (production only)
     * - Returns empty data structure on server errors
     *
     * **Performance Optimizations:**
     * - Base64 decoding for compressed XML payloads
     * - Parallel processing of scene tree traversal
     * - Lazy initialization of station statistics
     * - Memory-efficient string replacement for XML entities
     *
     * **Security:**
     * - User access mask filters resources by permission
     * - Private child scenes respect access control
     * - Credentials passed as Base64 encoded userId
     *
     * @example
     * ```typescript
     * // Full initial load after login
     * this.userService.getUserScenesData(user.businessId, NONE_VALUE)
     *   .subscribe(accountData => {
     *     console.log('Loaded scenes:', accountData.scenes.length);
     *     console.log('Loaded resources:', accountData.resources.length);
     *     console.log('Loaded stations:', accountData.stations.length);
     *     // Dispatch to store...
     *   });
     *
     * // Incremental sync with last changelist
     * this.userService.getUserScenesData(user.businessId, '12345')
     *   .subscribe(changes => {
     *     console.log('New/updated scenes:', changes.scenes.length);
     *     // Merge changes into store...
     *   });
     * ```
     *
     * @see IAccountData - Complete account data structure
     * @see IPlayerProps - Scene data structure
     * @see IResource - Resource metadata structure
     * @see IStation - Station configuration structure
     */
    getUserScenesData(businessId: BusinessId, i_changelistId): Observable<IAccountData> {
        let url: string;
        this.appSettings.workOffline ? url = this.appSettings.apiOfflineUrl + "data.json" : url = `${this.m_user.webApiBaseUrl}/Web/loadData.ashx?businessId=${this.m_user.businessId}&userId=${Lib.Base64().encode(this.m_user.username)}&fromChangelistId=${i_changelistId}`;
        let globalSettings: Array<IGlobalSettings>;
        let preferences: IPreferencesBusiness = {};
        let scenes: Array<IPlayerProps>;
        let resources: Array<IResource>;
        const stations: Array<IStation> = [];
        const liveStations: Array<IStation> = [];
        const stationsV1Stats: Array<IStationStatsV1> = [];
        const stationsV2Stats: Array<IStationStatsV1V2> = [];
        let transitionPools: Array<ITransitionPool>;
        let transitionPoolItems: Array<ITransitionPoolItem>;
        let musicChannels: Array<IMusicChannel>;
        let musicChannelSongs: Array<IMusicChannelSong>;
        let catalogItems: Array<ICatalogItems>;
        let catalogItemInfos: Array<ICatalogItemInfos>;
        let catalogItemResources: Array<ICatalogItemResources>;
        let categoryValues: Array<ICategoryValues>;
        let catalogItemCategories: Array<ICatalogItemCategories>;
        let eventParams: Array<IEventParam>;
        let eventScopes: Array<IEventScope> = [];
        let eventsStrings: Array<string> = [];
        let eventBlocks: Array<IEventBlock> = [];
        let iots: Array<IIot> = [];
        let schedules: Array<ISchedule>;
        let scheduleEvents: Array<IScheduleEvent>;
        let painters: Array<IPaintSource>;
        let trash: Array<IPlayerProps>;
        let trashResources: Array<IResource>;

        Lib.DevShow("scenes are loading " + url);
        return this._http.get<IPlayerProps[]>(url).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((playerData: IAccountData) => {
                if (isNullOrUndef(playerData) || playerData.status === false) {
                    this.toast.error(this.translocoService.translate("toast.problemLoadingAccount", {}, this.translocoService.getActiveLang()), playerData?.msg);
                    console.error(playerData?.msg);
                    if (playerData["msg"] && Lib.DevMode()) {
                        console.log(`Server Error user load: ${playerData["msg"]}`);
                    }
                    const accountDataNoData: IAccountData = {
                        changelistId: NONE_VALUE,
                        globalSettings,
                        resources,
                        scenes,
                        stations,
                        liveStations,
                        catalogItemInfos,
                        catalogItemResources,
                        catalogItems,
                        categoryValues,
                        eventParams,
                        eventScopes,
                        catalogItemCategories,
                        transitionPools,
                        transitionPoolItems,
                        musicChannels,
                        musicChannelSongs,
                        schedules,
                        scheduleEvents,
                        eventsStrings,
                        eventBlocks,
                        painters,
                    };
                    return accountDataNoData;
                }
                if (playerData.globalSettings) {
                    globalSettings = [];
                    Object.keys(playerData.globalSettings).forEach((key: string) => {
                        if (playerData.globalSettings[key].change_type === 3 && i_changelistId === NONE_VALUE || key == "-1") return;
                        globalSettings.push({
                            ...playerData.globalSettings[key],
                            param_id: key,
                            // enabled: 'true'
                        });
                    });
                }
                if (playerData.preferences) {
                    preferences = Lib.PreferencesProcess(playerData.preferences);
                    if (preferences && preferences?.twoFactorState?.value?.token?.length > 10) {
                        this.storeSelector.dispatch(actionTwoFactorCheckAccess({
                            payload: preferences.twoFactorState,
                            status: false,
                        }));
                    }
                }
                if (playerData.musicChannels) {
                    musicChannels = [];
                    Object.keys(playerData.musicChannels).forEach((key: string) => {
                        if (playerData.musicChannels[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.musicChannels[key]["music_channel_id"] = String(key);
                        playerData.musicChannels[key]["folder"] = playerData.musicChannels[key]?.tree_path;
                        musicChannels.push(playerData.musicChannels[key]);
                    });
                }
                if (playerData.musicChannelSongs) {
                    musicChannelSongs = [];
                    Object.keys(playerData.musicChannelSongs).forEach((key: string) => {
                        if (playerData.musicChannelSongs[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.musicChannelSongs[key]["music_channel_song_id"] = String(key);
                        musicChannelSongs.push(playerData.musicChannelSongs[key]);
                    });
                }
                if (playerData.transitionPools) {
                    transitionPools = [];
                    Object.keys(playerData.transitionPools).forEach((key: string) => {
                        if (playerData.transitionPools[key].change_type == 3 && i_changelistId === NONE_VALUE) return;
                        playerData.transitionPools[key]["id"] = String(key);
                        playerData.transitionPools[key]["handle"] = key;
                        transitionPools.push(playerData.transitionPools[key]);
                    });
                }
                if (playerData.transitionPoolItems) {
                    transitionPoolItems = [];
                    Object.keys(playerData.transitionPoolItems).forEach((key: string) => {
                        const poolItem = playerData.transitionPoolItems[key];
                        const cleanedData = poolItem.transition_item_data.replace("\ufeff", "");
                        xml2js.parseString(cleanedData, (err, transitionData) => {
                            if (err) {
                                this.storeSelector.sendEmail(ADMINISTRATOR, "error in transition json", this?.m_user?.email, true);
                                return;
                            }
                            if (poolItem.change_type == 3 && i_changelistId === NONE_VALUE) return;
                            transitionPoolItems.push({
                                transition_pool_item_id: key,
                                transition_pool_id: String(poolItem.transition_pool_id),
                                transition_item_name: poolItem.transition_item_name,
                                transition_item_data: transitionData,
                            });
                        });
                    });
                }
                if (playerData.eventParams) {
                    eventParams = [];
                    Object.keys(playerData.eventParams).forEach((key: string) => {
                        if (playerData.eventParams[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.eventParams[key]["event_param_id"] = key;
                        try {
                            playerData.eventParams[key]["event_value"] = JSON.parse(playerData.eventParams[key].event_value);
                        } catch (e) {
                            console.error("skipping event_value due to json error, event id: " + key + " error: " + e);
                            return;
                        }
                        if (playerData.eventParams[key]['event_type']) {
                            playerData.eventParams[key]['event_type'] = parseInt(playerData.eventParams[key]['event_type']);
                        } else {
                            playerData.eventParams[key]['event_type'] = 0;
                        }
                        eventParams.push(playerData.eventParams[key]);
                    });
                }
                if (playerData.iots && _.size(playerData.iots) > 0) {
                    iots = [];
                    Object.keys(playerData.iots).forEach((key: string) => {
                        if (playerData.iots[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.iots[key]["iot_id"] = parseInt(key);
                        iots.push(playerData.iots[key]);
                    });
                }
                if (playerData.eventScopes) {
                    eventScopes = [];
                    Object.keys(playerData.eventScopes).forEach((key: string) => {
                        if (playerData.eventScopes[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.eventScopes[key]["event_scope_id"] = key;
                        eventScopes.push(playerData.eventScopes[key]);
                    });
                }
                if (playerData?.resources && _.size(playerData?.resources) > 0) {
                    resources = [];
                    trashResources = [];
                    Object.keys(playerData?.resources || {}).forEach((key: string) => {
                        const resourceRow = playerData?.resources?.[key];
                        if (!resourceRow) return;
                        const resTypeLower = resourceRow?.resource_type?.toLowerCase?.();
                        // A migrated SWF/FLV original is kept in the scene by its own id (so the legacy player
                        // renders it), but carries converted_resource_id -> the PNG/WebM/MP4 the new player/Studio
                        // actually renders. swf/flv aren't natively-supported types, so keep ONLY those migrated
                        // sources in the store (raw, unconverted swf/flv are still filtered out) so block-loader
                        // sees the pointer and getResource can follow it to the converted resource.
                        const isMigratedSource = (resTypeLower === 'swf' || resTypeLower === 'flv') && resourceRow?.converted_resource_id != null;
                        if (!isMigratedSource && SUPPORTED_FILE_TYPES.indexOf(resTypeLower) === -1) return;
                        if (this.userMask && this.userMask?.findIndex((v) => compareIds(v, resourceRow?.access_key)) === -1) return;
                        const isTrashed = resourceRow?.change_type === 3;
                        if (resourceRow?.["default_player"] === "") {
                            const type = resourceRow?.["resource_type"]?.toLowerCase?.();
                            const blockLabel = this.storeSelector?.getBlockLabelByFileType?.(type);
                            if (blockLabel?.id) resourceRow["default_player"] = blockLabel.id;
                        }
                        if (isTrashed) {
                            if (i_changelistId === NONE_VALUE) trashResources?.push(resourceRow);
                            return;
                        }
                        resources?.push(resourceRow);
                    });
                }

                if (playerData.branchStations) {
                    Object.keys(playerData.branchStations).forEach((key: string) => {
                        if (playerData.branchStations[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        const _station = playerData.branchStations[key];
                        _station["station_id"] = key;
                        _station["version"] = 1;
                        _station["station_business_id"] = concatIdBusinessId(key, businessId, 1);
                        _station["player_data_id"] = String(_station["player_data_id"]);
                        _station["tree_path"] = _station["tree_path"];
                        stations.push(_station);
                    });
                }

                if (playerData.stations) {
                    Object.keys(playerData.stations).forEach((key: string) => {
                        if (playerData.stations[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.stations[key]["station_id"] = key;
                        playerData.stations[key]["version"] = 2;
                        playerData.stations[key]["station_business_id"] = concatIdBusinessId(key, businessId, "2");
                        playerData.stations[key]["tree_path"] = playerData.stations[key]["tree_path"];
                        playerData.stations[key]["player_data_id"] = String(playerData.stations[key]["player_data_id"]);
                        stations.push(playerData.stations[key]);
                        stationsV2Stats.push(getDefaultStationStats(concatIdBusinessId(key, businessId, "2"), key, "2"));
                    });
                    // for v2 stations we create a default station stats until later when we get real station stats from wss socket
                    this.storeSelector.dispatch(actionStationV1V2Update({ stationArray: stationsV2Stats }));
                }

                if (playerData.categoryValues) {
                    categoryValues = [];
                    Object.keys(playerData.categoryValues).forEach((key: string) => {
                        if (playerData.categoryValues[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        const parentId = isNullOrUndef(playerData.categoryValues[key].parent_category_value_id) ? -1 : playerData.categoryValues[key].parent_category_value_id;
                        categoryValues.push({
                            category_values_id: key,
                            parent_category_value_id: parentId,
                            category_value: playerData.categoryValues[key].category_value,
                        });
                    });
                }
                if (playerData.catalogItemCategories) {
                    catalogItemCategories = [];
                    Object.keys(playerData.catalogItemCategories).forEach((key: string) => {
                        if (playerData.catalogItemCategories[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        catalogItemCategories.push({
                            catalog_item_category_id: key,
                            catalog_item_id: playerData.catalogItemCategories[key].catalog_item_id,
                            category_value_id: playerData.catalogItemCategories[key].category_value_id,
                        });
                    });
                }
                if (playerData.catalogItemResources) {
                    catalogItemResources = [];
                    Object.keys(playerData.catalogItemResources).forEach((key: string) => {
                        if (playerData.catalogItemResources[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        catalogItemResources.push({
                            catalog_item_resource_id: key,
                            catalog_item_id: playerData.catalogItemResources[key].catalog_item_id,
                            resource_id: playerData.catalogItemResources[key].resource_id,
                        });
                    });
                }
                if (playerData.catalogItemInfos) {
                    catalogItemInfos = [];
                    Object.keys(playerData.catalogItemInfos).forEach((key: string) => {
                        if (playerData.catalogItemInfos[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        catalogItemInfos.push({
                            catalog_item_id: key,
                            info0: playerData.catalogItemInfos[key].info0,
                            info1: playerData.catalogItemInfos[key].info1,
                            info2: playerData.catalogItemInfos[key].info2,
                            info3: playerData.catalogItemInfos[key].info3,
                        });
                    });
                }
                if (playerData.catalogItems) {
                    catalogItems = [];
                    Object.keys(playerData.catalogItems).forEach((key: string) => {
                        if (playerData.catalogItems[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        catalogItems.push({
                            catalog_item_id: key,
                            item_name: playerData.catalogItems[key].item_name,
                        });
                    });
                }
                if (playerData.schedules) {
                    schedules = [];
                    Object.keys(playerData.schedules).forEach((key: string) => {
                        if (playerData.schedules[key].change_type === 3 && i_changelistId === NONE_VALUE || key == "-1") return;
                        schedules.push({
                            schedule_id: key,
                            schedule_name: playerData.schedules[key].schedule_name,
                            description: playerData.schedules[key].description,
                        });
                    });
                }
                if (playerData.scheduleEvents) {
                    scheduleEvents = [];
                    Object.keys(playerData.scheduleEvents).forEach((key: string) => {
                        if (playerData.scheduleEvents[key].change_type === 3 && i_changelistId === NONE_VALUE || key == "-1") return;
                        scheduleEvents.push({
                            ...playerData.scheduleEvents[key],
                            schedule_event_id: key,
                            // enabled: 'true'
                        });
                    });
                }
                if (playerData.painters) {
                    painters = [];
                    Object.keys(playerData.painters).forEach((key: string) => {
                        // Lib.DevShow('processing paint id: ' + key);
                        if (playerData.painters[key].change_type === 3 && i_changelistId === NONE_VALUE || key == "-1") return;
                        // if (this.userMask && this.userMask?.findIndex((v) => compareIds(v, playerData.painters[key].access_key)) === -1) return;
                        let painterData = playerData.painters[key]["painter_data"];
                        // var startTime = performance.now()
                        if (isBase64(painterData)) {
                            painterData = atob(painterData);
                        }
                        if (painterData === "undefined" || painterData === "" || painterData === "null") {
                            return;
                        }
                        // var endTime = performance.now()
                        // console.log(`Call to doSomething took ${endTime - startTime} milliseconds`);
                        let backgroundData = playerData.painters[key]["background"];
                        try {
                            if (painterData) painterData = JSON.parse(painterData);
                        } catch (err) {
                            console.log("Corrupted painter found, paint id " + key + " " + err.message);
                            if (Lib.DevMode()) {
                                alert("Corrupted painter found, paint id " + key + " " + err.message);
                            } else {
                                this.storeSelector.sendEmail(ADMINISTRATOR, "error in painter json", this?.m_user?.email, true);
                            }
                            return;
                        }
                        if (backgroundData) backgroundData = JSON.parse(backgroundData);
                        painters.push({
                            ...playerData.painters[key],
                            id: String(key),
                            painter_data: painterData,
                            background: backgroundData,
                        });
                    });
                }
                if (playerData?.scenes) {
                    scenes = [];
                    trash = [];
                    eventsStrings = [];
                    eventBlocks = [];
                    Object.keys(playerData?.scenes || {}).forEach((key: string, idx) => {
                        if (!playerData?.scenes?.[key]) return;
                        if (this.userMask && this.userMask?.findIndex((v) => compareIds(v, playerData?.scenes?.[key]?.access_key)) === -1) return;
                        const xmlData = playerData?.scenes?.[key]?.player_data_value;
                        const isTrashed = playerData?.scenes?.[key]?.change_type === 3;
                        // Lib.DevShow('processing scene id: ' + key);
                        xml2js.parseString(xmlData, { charkey: "_" }, (err, sceneData: IPlayerProps) => {
                            if (err || !sceneData) {
                                console.log("Corrupted scene found, scene id " + key + " " + err?.message);
                                if (Lib.DevMode()) {
                                    // alert("Corrupted scene found scene id " + key + " " + err?.message);
                                } else {
                                    /** only report errors for SignStudio as sometimes will get errors from components built in Pro, we just do not show them but not report issues **/
                                    if (this.studioType === EnumStudioType.SIGN_STUDIO) {
                                        this.storeSelector.sendEmail(ADMINISTRATOR, "error in scene json", this?.m_user?.email, true);
                                    }
                                }
                                return;
                            }
                            // inject IDs to src and id
                            // preProcessScene(sceneData, key);
                            // eventBlocks.push(...preProcessScene(sceneData, key));
                            const eventNames = preProcessSceneTree(sceneData, key);
                            if (eventNames.length > 0 && !isTrashed) {
                                eventsStrings = [...eventsStrings, ...eventNames];
                                eventsStrings = [...new Set(eventsStrings)];
                            }

                            if (!sceneData) {
                                Lib.DevShow("problem loading scene id: " + key);
                                return;
                            }
                            if (!sceneData?.Player) return;
                            if (!sceneData.Player.$) sceneData.Player.$ = {} as any;
                            sceneData.Player.$.folder = playerData?.scenes?.[key]?.tree_path;
                            sceneData.Player.$.accessKey = playerData?.scenes?.[key]?.access_key;
                            sceneData.Player.$.changelist_id = playerData?.scenes?.[key]?.changelist_id;
                            sceneData.Player.$.private_child = playerData?.scenes?.[key]?.private_child;
                            sceneData.Player.$.player_data_public = playerData?.scenes?.[key]?.player_data_public;
                            if (isTrashed) {
                                if (i_changelistId === NONE_VALUE) trash?.push(sceneData);
                                return;
                            }
                            scenes?.push(sceneData);

                            /******************************************** dev mode debugging **************************************************/
                            // if (Lib.DevMode() && sceneData.Player.$.label.toLowerCase() === 'weather simple xx')
                            if (Lib.DevMode() && sceneData.Player.$.label.toLowerCase() === "collection scene")
                                // if (Lib.DevMode() && sceneData.Player.$.label.toLowerCase() === '500px scene')
                                console.log("scene wip: " + sceneData.Player.$.label + " " + idx);
                            /******************************************************************************************************************/
                        });
                    });

                    // Save all scenes to individual files (localhost only)
                    // if (isHostLocal()) {
                    //     this.debugService.saveAllScenesToFiles(playerData, xml2js);
                    // }
                }
                const accountData: IAccountData = {
                    changelistId: playerData.changelistId,
                    globalSettings,
                    preferences,
                    resources,
                    trashResources,
                    scenes,
                    trash,
                    stations,
                    liveStations,
                    catalogItemInfos,
                    catalogItemResources,
                    catalogItems,
                    categoryValues,
                    catalogItemCategories,
                    schedules,
                    scheduleEvents,
                    transitionPools,
                    transitionPoolItems,
                    musicChannels,
                    musicChannelSongs,
                    eventParams,
                    eventScopes,
                    eventsStrings,
                    eventBlocks,
                    iots,
                    painters,
                };
                return accountData;
            }),
        );
    }

    /**
     * Generic method to call global service endpoints with or without authentication
     *
     * @description
     * Provides a unified interface for calling various endpoints on the global mediaCLOUD service.
     * Supports both authenticated and public endpoints, handling credentials automatically.
     *
     * @param i_endPoint - API endpoint path (e.g., 'getStats', 'updateProfile')
     * @param i_body - Request payload object
     * @param i_useCredentials - Whether to include authentication headers
     *
     * @returns Observable<Array<any>> - Response data from the endpoint
     *
     * @remarks
     * - Uses PUT for authenticated calls, POST for public calls
     * - Automatically adds authorization headers when i_useCredentials is true
     * - Includes retry logic with exponential backoff
     */
    getGlobalServiceData(i_endPoint: string, i_body: IGlobalDataRequest, i_useCredentials: boolean): Observable<Array<any>> {
        const headers = i_useCredentials ? getCredentialsHeader(this.m_user) : { headers: new HttpHeaders({ "Content-Type": "application/json" }) };
        const content = JSON.stringify(i_body);
        const url = `${this.appSettings.globalService}/${i_endPoint}`;
        const httpCall = i_useCredentials ? this._http.put<any>(url, content, headers) : this._http.post(url, {
            headers,
            responseType: "text" as "json",
            content,
        });
        return httpCall
            .pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err) => {
                    console.error("Error loading end point " + err);
                    this.onError(this.translocoService.translate("toast.sorryEncounteredProblem", {}, this.translocoService.getActiveLang()));
                    // return Observable.of(true);
                    return throwError(err);
                }),
                finalize(() => {
                }),
                map((result: any) => {
                    return result;
                }),
            );
    }

    // ============================================================================
    // SECTION: Station Management & Monitoring
    // ============================================================================

    /**
     * Retrieves geo-location data for all connected stations based on their IP addresses
     *
     * @description
     * Fetches geographic location information (latitude, longitude, city, country) for
     * digital signage player stations by querying their public IP addresses against a
     * geo-IP database. Adds slight randomization to coordinates for visual separation on maps.
     *
     * @param i_enterpriseMode - Whether running in enterprise mode (affects filtering)
     *
     * @returns Observable<Array<IStationGeoIp>> - Array of stations with geo-location data
     *
     * @remarks
     * - Only stations with public IPs and missing geo-location are queried
     * - Adds 0-0.30 random offset to coordinates to prevent exact overlap on maps
     * - Uses Node.js global service endpoint for IP lookups
     * - Falls back gracefully on errors without breaking app
     *
     * @example
     * ```typescript
     * this.userService.getServerStationsGeoIPs(false)
     *   .subscribe(stationsWithGeo => {
     *     stationsWithGeo.forEach(station => {
     *       console.log(`${station.id}: ${station.city}, ${station.country}`);
     *       console.log(`Coordinates: ${station.lat}, ${station.lon}`);
     *     });
     *   });
     * ```
     */
    getServerStationsGeoIPs(i_enterpriseMode: boolean): Observable<Array<IStationGeoIp>> {
        const stationsIps = [];
        let stationsGeoIps: Array<IStationGeoIp> = [];
        return this.storeSelector
            .getStationStats()
            .pipe(take(1),
                map((i_stations) => {
                    i_stations.forEach((i_station) => {
                        // if (i_station.$.connection == '1'){
                        //     alert(1);
                        // }
                        let ip = "";
                        if (!isNullOrUndefOrEmpty(i_station.$?.publicIp)) {
                            ip = i_station.$.publicIp;
                        } else if (!isNullOrUndefOrEmpty(i_station.$?.localIp)) {
                            ip = i_station.$.localIp;
                            // local ip 192.x.x.x
                            // } else if (!isNullOrUndefOrEmpty(i_station.$?.localAddress)) {
                            //     ip = i_station.$.localAddress;
                        } else if (!isNullOrUndefOrEmpty(i_station.$?.geoLocation?.ip)) {
                            ip = i_station.$.geoLocation.ip;
                        }
                        const geoLocation = i_station.$.geoLocation;
                        const id = i_station.$.id;
                        const businessId = i_station.$.businessId;
                        const source = i_station.$.source;
                        // only get stations with public ip and no location info
                        if (!isNullOrUndefOrEmpty(ip) && isNullOrUndefOrEmpty(geoLocation))
                            stationsIps.push({
                                id,
                                ip,
                                version: i_station.$.version,
                                source: this.m_user.sourceId,
                                businessId: isNullOrUndefOrEmpty(businessId) ? this.m_user.businessId : businessId,
                            });
                    });
                }),
                switchMap((v) => {
                    const content = JSON.stringify(stationsIps);
                    return this._http.post(`${this.appSettings.globalService}/getGeoByIp_v2`,
                        {
                            headers: new HttpHeaders({ "Content-Type": "application/json" }),
                            responseType: "text" as "json", content,
                        }).pipe(
                        catchError((err) => {
                            console.error("Error loading station IPs geo location " + err);
                            // this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                            // return Observable.of(true);
                            return throwError(err);
                        }),
                        finalize(() => {
                            // console.log('done!');
                        }),
                        map((result: any) => {
                            const stations = result;
                            // tslint:disable-next-line:forin
                            for (const station in stations) {
                                const i_station = stations[station];
                                const rand = _.random(0, 30) / 100;
                                i_station.station_business_id = concatIdBusinessId(stations[station].id, stations[station].businessId, stations[station].version);
                                i_station.lat = (i_station.lat + rand).toFixed(4);
                                i_station.lon = (i_station.lon + rand).toFixed(4);
                                i_station["city"] = i_station.city;
                                i_station["country"] = i_station.country;
                            }
                            return stationsGeoIps = stations;
                        }),
                    );
                }));
    }

    /**
     * Loads real-time statistics and status data for all v1 (legacy) stations
     *
     * @description
     * Fetches live status information for all connected digital signage players running
     * the v1 (legacy) protocol. This includes connection status, screen resolution, runtime
     * information, and other operational metrics. Data is returned in Base64 encoded XML
     * format which is decoded and parsed into station statistics objects.
     *
     * @param businessId - Business account ID
     *
     * @returns Observable<Array<IStationStatsV1>> - Array of v1 station statistics
     * @remarks
     * - Only applies to v1 (legacy) stations, not v2 stations
     * - Data is Base64 encoded in server response for compression
     * - XML parsing converts raw data to structured objects
     * - Includes retry logic for network resilience
     * - Logs successful loads to application logger
     *
     * @example
     * ```typescript
     * this.userService.getUserStationData(user.businessId)
     *   .subscribe(stationStats => {
     *     stationStats.forEach(stat => {
     *       console.log(`Station ${stat.$.id}: ${stat.$.connection === '1' ? 'Online' : 'Offline'}`);
     *     });
     *   });
     * ```
     */
    getUserStationData(businessId: BusinessId): Observable<Array<IStationStatsV1>> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "text" as "json",
        };

        const stationsUrl = `${this.m_user.webApiBaseUrl}/WebService/getStatus.ashx?user=${this.m_user.email}&password=${Lib.Base64().encode(this.m_user.passwordNew)}&base64=1&callback=?`;
        return this._http.get<IStationStatsV1[]>(stationsUrl, httpOptions).pipe(
            retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
            map((playerData64: any) => {
                const stations: IStationStatsV1[] = [];
                // const startTime = performance.now();
                let stationData = Lib.Base64().decode(playerData64.replace(/\?\({ "ret": "/, "").replace(/" }\)/, ""));
                stationData = stationData.replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[0-9a-fA-F]+);)/g, "&amp;");
                this.storeSelector.logger({ message: `loaded remote screens`, severity: 0 });
                xml2js.parseString(stationData, (err, i_stationData) => {
                    if (err) {
                        this.storeSelector.sendEmail(ADMINISTRATOR, "error in xml2js.parseString stations json", this?.m_user?.email, true);
                    }
                    // const endTime = performance.now();
                    // console.log(`Call to doSomething took ${endTime - startTime} milliseconds`);
                    if (i_stationData && i_stationData.Stations && i_stationData.Stations.Station && i_stationData.Stations.Station.length > 0) {
                        i_stationData.Stations.Station.forEach((k: IStationStatsV1, v) => {
                            k.$.businessId = `${businessId}`;
                            k.$.station_business_id = concatIdBusinessId(k.$.id, businessId, 1);
                            k.$.version = "1";
                            stations.push(k);
                        });
                    }
                });
                return stations;
            }),
        );
    }

    getUserStationLog(payload: IStationLogRequest): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const stationsUrl = `${this.m_user.webApiBaseUrl}/Web/log.ashx?&command=read&from=${payload.from}&user=${this.m_user.email}&pass=${Lib.Base64().encode(this.m_user.passwordNew)}&base64=1`;
        return this._http.get<IStationStatsV1[]>(stationsUrl, httpOptions).pipe(
            retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
            map((data: any) => {
                // this.storeSelector.logger({message: `loaded remote screens`, severity: 0});
                return data;
            }),
        );
    }

    getUserStationClearLog(payload: IStationLogRequest): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const stationsUrl = `${this.m_user.webApiBaseUrl}/Web/log.ashx?businessId=${payload.business_id}&command=clear&user=${this.m_user.email}&pass=${Lib.Base64().encode(this.m_user.passwordNew)}&base64=1`;
        return this._http.get<IStationStatsV1[]>(stationsUrl, httpOptions).pipe(
            retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
            map((data: any) => {
                return data;
            }),
        );
    }

    getUserStationStatistic(payload: any): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        let stationsUrl = `https://pop.signage.me/stationStats.ashx?command=read`;
        if (payload?.lastUpdate) {
            stationsUrl = `${stationsUrl}&lastUpdate=${payload.lastUpdate}`;
        }
        return this._http.post(stationsUrl, {
            user: this.m_user.email,
            pass: this.m_user.passwordNew,
        }, httpOptions).pipe(
            retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
            map((data: any) => {
                return data;
            }),
        );
    }

    feedMeAny(prompt: string, language: string, fields: any, series: string, dataType: string): Observable<IFeedMeAnyResponse> {
        /**
         * Internal function: Load mock feed data from local JSON files
         * This allows for faster testing without waiting for API responses
         */
        const getMockFeedData = (): Observable<IFeedMeAnyResponse> => {
            // Simulate API delay (1 second instead of several minutes)
            return of(null).pipe(
                delay(1000),
                switchMap(() => {
                    // Determine which mock file to load
                    // Since dataType is usually "text", infer from fields structure
                    let mockFileName = 'mock-feed-rss.json'; // Default

                    if (dataType === 'graph') {
                        mockFileName = 'mock-feed-chart.json';
                    } else if (!fields?.title || !fields?.description) {
                        mockFileName = 'mock-feed-json.json';
                    }

                    const mockFilePath = `/assets/sample/${mockFileName}`;
                    console.log(`[DEV] Loading mock data from: ${mockFilePath} (inferred from fields:`, fields, ')');

                    // Load the mock JSON file
                    return this._http.get<any>(mockFilePath).pipe(
                        map((mockData: any) => {
                            console.log('[DEV] Mock data loaded:', mockData);

                            // Return data in IFeedMeAnyResponse format
                            const response: IFeedMeAnyResponse = {
                                success: mockData.response?.success || true,
                                items: mockData.response?.items || [],
                                prompt: mockData.response?.prompt || mockData.prompt || prompt,
                                language: mockData.response?.language || mockData.language || language,
                                series: mockData.response?.series || [],
                            };

                            return response;
                        }),
                        catchError((error) => {
                            console.error('[DEV] Error loading mock data:', error);
                            console.log('[DEV] Falling back to default mock data');

                            // Fallback to basic mock data if file doesn't exist
                            const fallbackResponse: IFeedMeAnyResponse = {
                                success: true,
                                items: [],
                                prompt: prompt,
                                language: language,
                            };

                            return of(fallbackResponse);
                        })
                    );
                })
            );
        };

        // DEV MODE: Use mock data for faster testing
        if (isHostLocal()) {
            console.log('[DEV] Using mock data for feedMeAny, dataType:', dataType);
            return getMockFeedData();
        }

        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        // Category is Series
        // and Series are as Fields
        const body = {
            dataType: dataType || 'text',
            Prompt: prompt,
            Language: language,
            Fields: fields,
            Series: series || ''
        };
        const url = `https://ai.signage.me${isHostDevOrLocal() ? ":" + DEV_TEST_PORT : ""}/feed/request`;
        return this._http.post<IFeedMeAnyResponse>(url, body, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((data: IFeedMeAnyResponse) => {
                // DEV MODE: Save real API response to localStorage for mock data generation
                if (isHostDevOrLocal()) {
                    const mockData = {
                        timestamp: new Date().toISOString(),
                        dataType: dataType,
                        prompt: prompt,
                        language: language,
                        fields: fields,
                        series: series,
                        request: body,
                        response: data,
                    };
                    const key = `ai_feed_mock_${dataType}_${Date.now()}`;
                    localStorage.setItem(key, JSON.stringify(mockData, null, 2));
                    console.log(`[DEV] Saved real API response to localStorage: ${key}`, mockData);
                }
                return data;
            }),
        );
    }

    getFeedFields(prompt: string, language: string, numFields: number, dataType: string): Observable<IFeedFieldsResponse> {
        // DEV MODE: Use mock data for faster testing
        if (isHostLocal()) {
            return of(
                {
                    "success": true,
                    "prompt": "weekly weather forecast, show every day using number 1, 2, 3",
                    "dataType": "graph",
                    "requestedFields": 10,
                    "actualFields": 9,
                    "series": "Days of the week represented as numbers (1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday, 7=Sunday)",
                    "fields": {
                        "highTemperature": "Highest temperature expected for the day in Fahrenheit",
                        "lowTemperature": "Lowest temperature expected for the day in Fahrenheit",
                        "precipitationChance": "Probability of precipitation as a percentage (0-100%)",
                        "windSpeed": "Average wind speed in miles per hour",
                        "humidity": "Relative humidity level as a percentage (0-100%)",
                        "uvIndex": "Ultraviolet radiation index (0-11+ scale)",
                        "visibility": "Visibility distance in miles",
                        "dewPoint": "Dew point temperature in Fahrenheit",
                        "cloudCover": "Cloud coverage as a percentage (0-100%)"
                    },
                }
            )
        }

        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        // Category is Series
        // and Series are as Fields
        const body = {
            dataType: dataType || 'text',
            Prompt: prompt,
            Language: language,
            numFields: numFields
        };
        const url = `https://ai.signage.me${isHostDevOrLocal() ? ":"  + DEV_TEST_PORT : ""}/feed/getFields`;
        return this._http.post<IFeedFieldsResponse>(url, body, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((data: IFeedFieldsResponse) => {
                // DEV MODE: Save real API response to localStorage for mock data generation
                if (isHostDevOrLocal()) {
                    // const mockData = {
                    //     timestamp: new Date().toISOString(),
                    //     dataType: dataType,
                    //     prompt: prompt,
                    //     language: language,
                    //     fields: fields,
                    //     series: series,
                    //     request: body,
                    //     response: data,
                    // };
                    // const key = `ai_feed_mock_${dataType}_${Date.now()}`;
                    // localStorage.setItem(key, JSON.stringify(mockData, null, 2));
                    // console.log(`[DEV] Saved real API response to localStorage: ${key}`, mockData);
                }
                return data;
            }),
        );
    }

    /**
     * Reseller self-upgrade eligibility. Calls MasterWeb getCustomerInfo.ashx (same galaxy origin as Studio,
     * no mars/node) which returns canSelfUpgrade = accountStatusReseller != 4 AND the reseller's last charge
     * was > 6 months ago. Drives whether the self-upgrade rocket shows for a customer under a lapsed reseller.
     */
    public getSelfUpgradeEligible(businessId: string): Observable<any> {
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/Web/getCustomerInfo.ashx?businessId=${businessId}`;
        return this._http.get<any>(url).pipe(
            catchError((e) => {
                return of({error: "Error getSelfUpgradeEligible " + (e.message || e)});
            }),
            map((v: any) => v),
        );
    }

    public getPreferences(i_user: IUser, globalService: string): Observable<any> {
        const header = getCredentialsHeader(i_user);
        const url = `${globalService}/getPreferences`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem getStudioProUrlV2 " + e);
            }),
            map((v: any) => {
                return v;
            }),
        );
    }

    getAccountStatistic(): Observable<any> {
        const stationsUrl = `https://galaxy.signage.me/Web/getReport.ashx?command=createConversion&group=monthly`;
        return this._http.get(
            stationsUrl,
            {
                params: {
                    user: this?.m_user?.email,
                    pass: this?.m_user?.passwordNew,
                },
            })
            .pipe(
                retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
                catchError((e) => {
                    return of(null);
                }),
                map((data: any) => {
                    return data;
                }),
            );
    }

    getSharedAccountContent(): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const stationAddUrl = `https://galaxy.signage.me/Web/getSiblingBusinesses.ashx`;
        return this._http.post(stationAddUrl,
            {
                user: this.m_user.email,
                pass: this.m_user.passwordNew,
            },
            httpOptions,
        ).pipe(
            retryWithBackoff(stationAddUrl, this.storeSelector, this.toast),
            catchError((e) => of(null)),
            mergeMap((servers: any) => {
                // "webTokens" is a businessId -> web_token map (not a server); pull it out and
                // stamp each shared scene with its OWN business web_token so previews validate.
                const webTokens = (servers && servers.webTokens) || {};
                // "studioInfo" is a businessId -> { studioType, migrationStatus, migrationDate } map
                // (not a server); stamp each shared scene so the table can show a Sign/Legacy column.
                const studioInfo = (servers && servers.studioInfo) || {};
                // "businessNames" is a businessId -> business_name map (not a server); stamp each shared
                // scene so the table can show a friendly account name next to the numeric business id.
                const businessNames = (servers && servers.businessNames) || {};
                const serverIds = Object.keys(servers || {}).filter((k) => k !== "webTokens" && k !== "studioInfo" && k !== "businessNames");
                return forkJoin(
                    serverIds.map((serverId: any) => {
                        const server = SERVERS.find((v) => compareIds(v.source_id, serverId));
                        const resources = servers[serverId];
                        const url = `https://${server.name}.signage.me/Web/getSharedContents.ashx`;
                        return this._http.post(url, resources, httpOptions).pipe(
                            retryWithBackoff(url, this.storeSelector, this.toast),
                            catchError((e) => {
                                console.error("Error getSharedAccountContent server data", e);
                                return of(null);
                            }),
                            map((data: any) => {
                                if (data?.scenes?.length) {
                                    data.scenes.forEach((row: any) => {
                                        row.webToken = webTokens[row.businessId] ?? webTokens[String(row.businessId)] ?? "";
                                        const studio = studioInfo[row.businessId] ?? studioInfo[String(row.businessId)];
                                        if (studio) {
                                            row.studioType = studio.studioType;
                                            row.migrationStatus = studio.migrationStatus;
                                            row.migrationDate = studio.migrationDate;
                                        }
                                        row.businessName = businessNames[row.businessId] ?? businessNames[String(row.businessId)] ?? "";
                                    });
                                }
                                return { url, data };
                            }),
                        );
                    }),
                );
            }),
        );
    }

    registerStationFromCode(reqCode, sceneId, stationName): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const stationAddUrl = `https://galaxy.signage.me/Web/registerPlayer.ashx`;
        return this._http.post(stationAddUrl,
            {
                user: this.m_user.email,
                pass: this.m_user.passwordNew,
                sceneId,
                reqCode,
                stationName,
            },
            httpOptions,
        ).pipe(
            retryWithBackoff(stationAddUrl, this.storeSelector, this.toast),
            catchError((e) => {
                return of(null);
            }),
            map((data: any) => {
                return data;
            }),
        );
    }

    clearStationStatistic(): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `https://pop.signage.me/stationStats.ashx?command=clear`;
        return this._http.post(url,
            {
                user: this.m_user.email,
                pass: this.m_user.passwordNew,
            },
            httpOptions,
        ).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of(null);
            }),
            map((data: any) => {
                return data;
            }),
        );
    }

    // public getUserStationDataNext(businessId: BusinessId): Observable<Array<IStationStats>> {
    //     const observables: Array<Observable<any>> = [];
    //     // tslint:disable-next-line:forin
    //     const stationsUrls = [
    //         `${this.m_user.webApiBaseUrl}/WebService/getStatus.ashx?user=${this.m_user.email}&password=${this.m_user.passwordNew}&callback=?`,
    //         `${this.m_user.webApiBaseUrl}/WebService/getStatus.ashx?user=${this.m_user.email}&password=${this.m_user.passwordNew}&callback=?`
    //     ];
    //     const httpOptions = {
    //         headers: new HttpHeaders({'Content-Type': 'application/json'}),
    //         responseType: 'text' as 'json'
    //     };
    //     // tslint:disable-next-line
    //     for (const u in stationsUrls) {
    //         const url = stationsUrls[u];
    //         observables.push(
    //             this._http.get<IStationStats[]>(url, httpOptions).pipe(
    //                 retryWithBackoff(url, this.storeSelector, this.toast),
    //                 map((playerData64: any) => {
    //                     const stations: IStationStats[] = [];
    //                     const stationData = Lib.Base64().decode(playerData64.replace(/\?\({ "ret": "/, '').replace(/" }\)/, ''));
    //                     this.storeSelector.logger({message: `loaded remote screens`, severity: 0});
    //                     xml2js.parseString(stationData, (err, i_stationData) => {
    //                         if (i_stationData && i_stationData.Stations && i_stationData.Stations.Station && i_stationData.Stations.Station.length > 0) {
    //                             i_stationData.Stations.Station.forEach((k: IStationStats, v) => {
    //                                 k.$.businessId = `${this.m_user.businessId}`;
    //                                 k.$.station_business_id = concatIdBusinessId(k.$.id, this.m_user.businessId);
    //                                 stations.push(k);
    //                             });
    //                         }
    //                     });
    //                     return stations;
    //                 })
    //             )
    //         );
    //     }
    //     return forkJoin(observables).pipe(
    //         map((val) => {
    //             return val;
    //         }));
    // }


    // ============================================================================
    // SECTION: Authentication & User Management
    // ============================================================================

    /**
     * Verifies social login tokens (Google, Facebook, LDAP, Amazon) and retrieves user profile
     *
     * @description
     * Validates OAuth tokens from various social login providers and retrieves the associated
     * user profile information. Supports Google, Facebook, LDAP enterprise login, and Amazon
     * authentication. The method routes to different endpoints based on the provider and
     * returns standardized user profile data.
     *
     * @param i_socialUser - Social user object containing provider details and auth token
     * @param i_socialUser.provider - Auth provider ('GOOGLE', 'FACEBOOK', 'LDAP', 'AMAZON')
     * @param i_socialUser.idToken - OAuth ID token (Google)
     * @param i_socialUser.authToken - OAuth access token (Facebook)
     *
     * @returns Observable<ISocialServerReply> - Server reply with user profile and MS account info
     *
     * @remarks
     * **Provider-Specific Handling:**
     * - **GOOGLE**: Verifies ID token via `/googleAuth/{idToken}` endpoint
     * - **FACEBOOK**: Validates access token via `/auth/facebook/token` endpoint
     * - **LDAP**: Verifies enterprise credentials via `/ldapAuthVerify/{base64Token}` endpoint
     * - **AMAZON**: Returns profile directly without server verification (special case)
     *
     * **Response Data:**
     * - socialUser: Original social user object
     * - profile: User profile with name, email, company
     * - isReseller: Whether user is a reseller account
     * - msUser: MediaSignage username (if account exists)
     * - msPassword: MediaSignage password (if account exists)
     * - exists: Whether MediaSignage account already exists
     *
     * @example
     * ```typescript
     * const googleUser: SocialUser = {
     *   provider: 'GOOGLE',
     *   id: 'google-user-id',
     *   email: 'user@example.com',
     *   name: 'John Doe',
     *   idToken: 'eyJhbGciOiJSUzI1...',
     *   // ... other Google user fields
     * };
     *
     * this.userService.verifySocialToken(googleUser)
     *   .subscribe(reply => {
     *     if (reply.exists) {
     *       console.log('Existing user:', reply.msUser);
     *     } else {
     *       console.log('New user, create account');
     *     }
     *   });
     * ```
     */
    verifySocialToken(i_socialUser: SocialUser): Observable<ISocialServerReply> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        let socialUrl = "";
        if (i_socialUser.provider === "AMAZON") {
            console.log("LOG: AMAZON");
            socialUrl = "";
        } else if (i_socialUser.provider === "LDAP") {
            console.log("LOG: LDAP");
            const token = jsonToBase64(i_socialUser);
            socialUrl = `/ldapAuthVerify/${token}`;
        } else if (i_socialUser.provider === "GOOGLE") {
            console.log("LOG: GOOGLE");
            socialUrl = `/googleAuth/${i_socialUser.idToken}`;
        } else if (i_socialUser.provider === "FACEBOOK") {
            console.log("LOG: FACEBOOK");
            socialUrl = `/auth/facebook/token?access_token=${i_socialUser.authToken}`;
        } else {
            alert("error: problem detecting login type 1");
        }
        if (i_socialUser.provider === "AMAZON") {
            console.log("LOG: AMAZON");
            const amazonAuthUser: ISocialServerReply = {
                socialUser: i_socialUser,
                profile: {
                    access: {
                        user: i_socialUser.firstName,
                        name: i_socialUser.name,
                        company: "",
                    },
                },
                isReseller: null,
                msUser: null,
                msPassword: null,
                exists: null,
            };
            return of(amazonAuthUser);
        }

        // if (i_socialUser.provider === 'GOOGLE') {
        //     const googleAuthUser: ISocialServerReply = {
        //         socialUser: i_socialUser,
        //         profile: {
        //             access: {
        //                 user: i_socialUser.firstName,
        //                 name: i_socialUser.name,
        //                 company: ''
        //             }
        //         },
        //         isReseller: null,
        //         msUser: null,
        //         msPassword: null,
        //         exists: null
        //     };
        //     return of(googleAuthUser);
        // }

        /** facebook google and ldap now require token auth **/
        const url = `${this.appSettings.globalService}${socialUrl}`;
        return this._http.get<ISocialServerReply>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ISocialServerReply) => {
                return v;
            }),
        );
    }


    //// we only need this section if we bring back email verification..........................
    //  accountStatus(businessId): Observable<IAccountReply> {
    //   const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=GetAccountStatus&businessId=${businessId}`;
    //   return this._http.jsonp<any[]>(url, 'callback').pipe(
    //     retryWithBackoff(),
    //     map(accountStatus => {
    //       return {
    //         status: {
    //           businessId: String(businessId),
    //           accountStatus
    //         }
    //       };
    //     })
    //   );
    // }

    /**
     * Fetches complete business user information including reseller config and privileges
     *
     * @description
     * **This is the primary authentication and user info loading method.**
     * Called during login to retrieve the user's business account details, reseller configuration,
     * privilege settings, and account status. This v3 version fixes bugs in v2 where payer_id
     * sometimes returned -1 incorrectly. The method decodes Base64 encoded XML configurations
     * for enterprise reseller studios and privilege information.
     *
     * @param i_user - Username/email for authentication
     * @param i_pass - User password
     * @param i_billingToken - Optional billing token for payment-related operations (default: null)
     *
     * @returns Observable<IUserSync> - Complete user sync object with account details
     *
     * @remarks
     * **Response Processing:**
     * - Decodes Base64 encoded privilege information XML
     * - Parses custom studio XML for enterprise resellers
     * - Handles PLUS account reseller ID associations
     * - Includes artificial delay for rate limiting (500ms dev, 1500ms production)
     *
     * **Data Included:**
     * - accountReplyV2.businessUserInfo: Core business user details
     * - privilegeInfo: User permission settings (parsed from XML)
     * - resellerInfo.customStudio: Enterprise studio configuration (parsed from XML)
     * - Account version, type, and status information
     *
     * **Error Handling:**
     * - Returns status: 'fail' on authentication failure
     * - Sends admin email on XML parsing errors
     * - Shows user toast notification on errors
     *
     * @example
     * ```typescript
     * // Standard login
     * this.userService.getBusinessUserInfoV3('user@example.com', 'password123')
     *   .subscribe(userSync => {
     *     if (userSync.status === 'fail') {
     *       console.log('Login failed');
     *     } else {
     *       console.log('User loaded:', userSync.accountReplyV2.businessUserInfo);
     *     }
     *   });
     * // Login with billing token (for payment operations)
     * this.userService.getBusinessUserInfoV3('user@example.com', 'password123', 'billing_token_abc')
     *   .subscribe(userSync => {
     *     // Process user with billing context
     *   });
     * ```
     *
     * @see IUserSync - Complete user synchronization data structure
     * @see IAccountReplyV2 - Account reply format v2
     */
    getBusinessUserInfoV3(i_user, i_pass, i_billingToken = null): Observable<IUserSync> {
        const wait = Lib.DevMode() ? 500 : 1500;
        const master = `${this.appSettings.protocol}${this.appSettings.masterServer}`;
        const url = `${this.appSettings.globalService}/dwBusinessUserInfoV3?masterUrl=${master}`;
        let plusResellerId = null;
        if (this.m_user) {
            if (Lib.GetAccountVersion(this.m_user) === "PLUS") {
                plusResellerId = this.m_user.resellerId;
            }
        }
        const body = {
            user: i_user,
            pass: i_pass,
            billingToken: i_billingToken,
            plusResellerId,
        };
        return this._http.post<IAccountReplyV2>(url, body, httpOptionsJson).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IUserSync) => {
                if (v.status === "fail") return v;
                const privilegeInfoXml = Lib.Base64().decode(v.accountReplyV2.businessUserInfo.privilegeInfo);
                // const customStudio = Lib.Base64().decode(v.businessUserInfo.resellerInfo.customStudio);
                // const privilegeId = v.businessUserInfo.privilegeId;
                // const accessMask = v.businessUserInfo.accessMask;
                if (v.accountReplyV2.businessUserInfo.resellerInfo && v.accountReplyV2.businessUserInfo.resellerInfo.customStudio) {
                    const playerData = Lib.Base64().decode(v.accountReplyV2.businessUserInfo.resellerInfo.customStudio);
                    xml2js.parseString(playerData, (err, resellerXml) => {
                        if (err) {
                            console.error("problem decoding enterprise studio xml");
                            this.storeSelector.sendEmail(ADMINISTRATOR, "error in business info json", this?.m_user?.email, true);
                            this.toast.error(this.translocoService.translate("toast.sorryEncounteredProblem", {}, this.translocoService.getActiveLang()));
                            return throwError(err + " problem decoding enterprise studio xml");
                        }
                        v.accountReplyV2.businessUserInfo.resellerInfo.customStudio = resellerXml;
                        if (privilegeInfoXml) {
                            xml2js.parseString(privilegeInfoXml, (err2, privilegeInfo) => {
                                if (err2) {
                                    console.error("problem decoding enterprise studio privilegeInfo xml");
                                    this.storeSelector.sendEmail(ADMINISTRATOR, "error in business info json", this?.m_user?.email, true);
                                    this.toast.error(this.translocoService.translate("toast.sorryEncounteredProblem", {}, this.translocoService.getActiveLang()));
                                    return throwError(err + " problem decoding enterprise studio privilegeInfo xml");
                                }
                                v.accountReplyV2.businessUserInfo.privilegeInfo = privilegeInfo;
                            });
                        }
                    });
                }
                return v;
            }),
        );
    }

    /**
     * Makes a custom PUT request to any URL with data payload
     *
     * @description
     * Generic utility method for making authenticated PUT requests to custom endpoints.
     * Useful for one-off API calls that don't fit standard patterns.
     *
     * @param url - Target URL endpoint
     * @param data - Request payload data
     *
     * @returns Observable<any> - Server response data
     *
     * @remarks
     * - Includes retry logic with exponential backoff
     * - Returns error string on failure instead of throwing
     * - Uses JSON content type
     *
     * @example
     * ```typescript
     * const url = 'https://api.example.com/custom';
     * const data = { field1: 'value1', field2: 'value2' };
     * this.userService.customUrl(url, data)
     *     .subscribe(response => console.log(response));
     * ```
     */
    public customUrl(url, data): Observable<any> {
        const header = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        return this._http.put<any>(url, data, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem customUrlPut put " + e);
            }),
            map((v: any) => v),
        );
    }

    // ============================================================================
    // SECTION: Social Media & Authentication Services
    // ============================================================================

    /**
     * Creates a new social media service profile for the current user
     *
     * @description
     * Initializes a social media integration profile that allows the user to connect
     * social media accounts (Facebook, Twitter, Instagram, etc.) for posting content
     * directly from SignStudio scenes.
     *
     * @returns Observable<any> - Profile creation response with profile ID
     *
     * @remarks
     * - Requires authenticated user
     * - Uses user credentials from store
     * - Creates empty profile that needs to be populated with social tokens
     * - Includes retry logic for network resilience
     *
     * @example
     * ```typescript
     * this.userService.createSocialServiceProfile()
     *     .subscribe(response => {
     *         console.log('Social profile created:', response.profileId);
     *     });
     * ```
     */
    public createSocialServiceProfile(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/createSocialProfile`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem createSocialServiceProfile post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Authorizes Canva integration by exchanging authorization code for access token
     *
     * @description
     * Sends the Canva OAuth authorization code to the server to exchange it for
     * an access token. The server handles the token exchange with Canva's API
     * and stores the credentials for future API calls.
     *
     * @param code - The authorization code received from Canva OAuth flow
     * @returns Observable<any> - Object containing { success, token, error }
     *
     * @remarks
     * - Called after user authorizes the app in Canva popup window
     * - Server exchanges code for access token with Canva API
     * - Token is stored server-side for making authenticated Canva API calls
     * - Returns success status and token information
     *
     * @example
     * ```typescript
     * this.userService.authorizeCanva(authCode)
     *     .subscribe(response => {
     *         if (response.success) {
     *             console.log('Canva authorized successfully');
     *         }
     *     });
     * ```
     */
    public authorizeCanva(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/canva/authorize`;
        // const url = `http://127.0.0.1:3000/canva/authorize`;
        const body = {};
        return this._http.post<any>(url, body, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "problem authorizeCanva post " + e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Gets the Canva reference ID for the current user
     *
     * @description
     * Retrieves the unique Canva reference ID associated with the current user's account.
     * This ID is used to link the user's SignStudio account with their Canva integration
     * and is required for making authenticated Canva API calls.
     *
     * @returns Observable<any> - Object containing { success, refId, error }
     *
     * @remarks
     * - Called to retrieve the stored Canva reference ID
     * - Returns the refId if user has already authorized Canva
     * - Returns error if user has not yet authorized Canva integration
     * - This ID is generated during the initial Canva authorization flow
     *
     * @example
     * ```typescript
     * this.userService.getCanvaRefId()
     *     .subscribe(response => {
     *         if (response.success) {
     *             console.log('Canva Ref ID:', response.refId);
     *         }
     *     });
     * ```
     */
    public getCanvaRefId(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/canva/getRefId`;
        // const url = `http://127.0.0.1:3000/canva/getRefId`;
        return this._http.get<any>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "problem getCanvaRefId get " + e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Gets Canva designs for the specified reference ID
     *
     * @description
     * Retrieves all Canva designs associated with a specific Canva reference ID.
     * This allows users to browse and select from their Canva designs to import
     * into SignStudio scenes.
     *
     * @param refId - The Canva reference ID to fetch designs for
     * @returns Observable<any> - Object containing { success, designs, error }
     *
     * @remarks
     * - Requires user to be authorized with Canva (refId must exist)
     * - Returns an array of design objects with metadata (id, name, thumbnail, etc.)
     * - Designs can be filtered, searched, or displayed in a gallery
     * - Each design object contains information needed to embed or import the design
     *
     * @example
     * ```typescript
     * this.userService.getCanvaDesigns(refId)
     *     .subscribe(response => {
     *         if (response.success) {
     *             console.log('Canva Designs:', response.designs);
     *             // Display designs in UI for user selection
     *         }
     *     });
     * ```
     */
    public getCanvaDesigns(refId: string): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/canva/designs/${refId}`;
        // const url = `http://127.0.0.1:3000/canva/designs/${refId}`;
        return this._http.get<any>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "problem getCanvaDesigns get " + e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Revokes Canva authorization and access token
     *
     * @description
     * Revokes the user's Canva authorization by deleting the access token and
     * disconnecting the integration. This will require the user to re-authorize
     * if they want to use Canva integration again.
     *
     * @param refId - The Canva reference ID to revoke authorization for
     * @returns Observable<any> - Object containing { success, message, error }
     *
     * @remarks
     * - Requires user to be authorized with Canva (refId must exist)
     * - Deletes the access token on both client and Canva sides
     * - User will need to re-authorize to use Canva features again
     * - Returns success status and confirmation message
     *
     * @example
     * ```typescript
     * this.userService.revokeCanvaAuth(refId)
     *     .subscribe(response => {
     *         if (response.success) {
     *             console.log('Canva authorization revoked');
     *         }
     *     });
     * ```
     */
    public revokeCanvaAuth(refId: string): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/canva/revoke/${refId}`;
        // const url = `http://127.0.0.1:3000/canva/revoke/${refId}`;
        return this._http.post<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "problem revokeCanvaAuth post " + e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Generates a new Two-Factor Authentication (2FA) secret key and QR code
     *
     * @description
     * Initiates 2FA setup for the current user by generating a new TOTP secret key
     * and QR code that can be scanned with authenticator apps (Google Authenticator,
     * Authy, etc.). This is the first step in enabling 2FA for account security.
     *
     * @returns Observable<any> - Object containing { secret, qrCode, backupCodes }
     *
     * @remarks
     * - Generates time-based one-time password (TOTP) secret
     * - Returns QR code as data URL for easy scanning
     * - Includes backup recovery codes for account recovery
     * - Must be followed by `twoFactorAuth()` to verify and activate 2FA
     * - Secret is NOT saved until verification succeeds
     *
     * @example
     * ```typescript
     * this.userService.twoFactorGen()
     *     .subscribe(response => {
     *         console.log('Secret:', response.secret);
     *         console.log('QR Code:', response.qrCode); // Display this image
     *         console.log('Backup codes:', response.backupCodes);
     *         // Show QR code to user, then verify with twoFactorAuth()
     *     });
     * ```
     */
    public twoFactorGen(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/twoFactorGen`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem twoFactorGen post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Verifies and activates Two-Factor Authentication for the user account
     *
     * @description
     * Validates a TOTP code from the user's authenticator app to confirm 2FA setup.
     * If the code matches, 2FA is permanently enabled for the account and all future
     * logins will require both password and authenticator code.
     *
     * @param value - Object containing { payload: { token: string, secret: string } }
     * @param value.payload.token - 6-digit TOTP code from authenticator app
     * @param value.payload.secret - Secret key generated by twoFactorGen()
     *
     * @returns Observable<any> - { success: boolean, message: string }
     *
     * @remarks
     * - Code must be entered within 30-second TOTP window
     * - On success, 2FA is permanently enabled
     * - On failure, returns error but allows retry
     * - Secret is saved to user preferences on successful verification
     * - Dispatches actionTwoFactorCheckAccess to update store
     *
     * @example
     * ```typescript
     * const payload = {
     *     payload: {
     *         token: '123456', // From authenticator app
     *         secret: 'JBSWY3DPEHPK3PXP' // From twoFactorGen()
     *     }
     * };
     * this.userService.twoFactorAuth(payload)
     *     .subscribe(response => {
     *         if (response.success) {
     *             console.log('2FA enabled successfully!');
     *         } else {
     *             console.error('Invalid code, try again');
     *         }
     *     });
     * ```
     */
    public twoFactorAuth(value): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/twoFactorAuth`;
        return this._http.put<any>(url, value.payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem twoFactorAuth post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Saves a preference record via mars/Node for reseller/enterprise users.
     * Resellers do not have a webApiBaseUrl so they cannot use the standard
     * crudRecord save path through IIS.
     */
    public savePreferenceViaNode(body: { preference_key: string, preference_value: any, user_id: string }): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/savePreference`;
        return this._http.put<any>(url, body, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({status: false, error: "problem savePreferenceViaNode " + e});
            }),
            map((v: any) => v),
        );
    }

    /**
     * Posts content to connected social media platforms
     *
     * @description
     * Publishes text, images, or videos to one or more connected social media accounts
     * (Facebook, Twitter, Instagram, LinkedIn, etc.) directly from SignStudio. Supports
     * multi-platform simultaneous posting with media attachments.
     *
     * @param val - Social post configuration object
     * @param val.socialKey - Social token containing refId and profileKey
     * @param val.socialKey.refId - Reference ID for the social profile
     * @param val.socialKey.profileKey - Profile key for authentication
     * @param val.platforms - Array of platform identifiers to post to (e.g., ['facebook', 'twitter', 'instagram'])
     * @param val.previewUrl - URL to preview image (for link previews)
     * @param val.resourceUrl - URL to media resource (image/video to attach)
     * @param val.text - Post text content/caption
     *
     * @returns Observable<any> - Post results with platform-specific responses
     *
     * @remarks
     * **Supported Platforms:**
     * - Facebook (pages, groups, profiles)
     * - Twitter/X
     * - Instagram
     * - LinkedIn
     * - Pinterest
     * - Google Business
     * - TikTok
     * - Reddit
     * - Telegram
     * - YouTube
     *
     * **Media Support:**
     * - Images: Uploaded as attachments
     * - Videos: Uploaded to supported platforms
     * - Preview URLs: For link sharing
     *
     * **Response Structure:**
     * Returns array of platform-specific responses with post IDs and URLs
     *
     * @example
     * ```typescript
     * const socialPost = {
     *     socialKey: { refId: '123', profileKey: 'abc' },
     *     platforms: ['facebook', 'twitter', 'instagram'],
     *     text: 'Check out our latest digital signage content!',
     *     previewUrl: 'https://example.com/preview.jpg',
     *     resourceUrl: 'https://example.com/media.jpg'
     * };
     * this.userService.postSocialServiceProfile(socialPost)
     *     .subscribe(results => {
     *         results.forEach(result => {
     *             console.log(`Posted to ${result.platform}: ${result.postUrl}`);
     *         });
     *     });
     * ```
     */
    public postSocialServiceProfile(
        val: {
            socialKey: ISocialToken,
            platforms: Array<any>;
            previewUrl: string;
            resourceUrl: string;
            text: string
        } & Action<string>): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/postSocial/${val.socialKey.refId}`;
        return this._http.put<any>(url, {
            text: val.text,
            platforms: val.platforms,
            previewUrl: val.previewUrl,
            resourceUrl: val.resourceUrl,
        }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem postSocialServiceProfile post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Generates OAuth authorization URL for social media platform connection
     *
     * @description
     * Creates the OAuth authorization URL that users must visit to grant SignStudio
     * permission to post on their behalf. This is part of the OAuth 2.0 flow for
     * connecting social media accounts.
     *
     * @param keys - Social token object
     * @param keys.refId - Reference ID for tracking this authorization
     * @param keys.profileKey - Profile key for identifying the user's social profile
     *
     * @returns Observable<any> - Object containing { authUrl, state, expiresIn }
     *
     * @remarks
     * **OAuth Flow:**
     * 1. Call this method to get authorization URL
     * 2. Redirect user to the authUrl
     * 3. User grants permissions on social platform
     * 4. Platform redirects back with authorization code
     * 5. Exchange code for access token (handled server-side)
     *
     * **Response Properties:**
     * - `authUrl`: URL to redirect user for authorization
     * - `state`: CSRF protection token
     * - `expiresIn`: Token expiration time (seconds)
     *
     * @example
     * ```typescript
     * const keys = { refId: '123', profileKey: 'user_social_profile' };
     * this.userService.createSocialCreateURL(keys)
     *     .subscribe(response => {
     *         console.log('Authorization URL:', response.authUrl);
     *         // Redirect user to authorization URL
     *         window.location.href = response.authUrl;
     *     });
     * ```
     */
    public createSocialCreateURL(keys: ISocialToken): Observable<any> {
        console.log("createSocialCreateURL " + JSON.stringify(keys));
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/createSocialCreateURL/${keys.refId}/${keys.profileKey}`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem createSocialCreateURL post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Removes a connected social media profile/token from user's account
     *
     * @description
     * Disconnects a previously authorized social media account by removing its access token.
     * This revokes SignStudio's permission to post to that platform and deletes the stored
     * credentials. User must re-authorize if they want to reconnect later.
     *
     * @param val - Object containing { socialKey: string, paramId: string }
     * @param val.socialKey - Unique key identifying the social profile to remove
     * @param val.paramId - Parameter ID for tracking the removal operation
     *
     * @returns Observable<ISocialReply> - Removal confirmation with updated profile list
     *
     * @remarks
     * - Permanently removes access token for the specified platform
     * - Does NOT revoke permissions on the social platform itself (user must do that separately)
     * - Returns updated list of remaining connected profiles
     * - paramId is preserved in response for UI tracking
     * - Safe to call multiple times (idempotent)
     *
     * @example
     * ```typescript
     * const removal = {
     *     socialKey: 'facebook_12345',
     *     paramId: 'profile_1'
     * };
     * this.userService.loadSocialRemoveKey(removal)
     *     .subscribe(response => {
     *         console.log('Profile removed:', removal.socialKey);
     *         console.log('Remaining profiles:', response.profiles);
     *     });
     * ```
     */
    public loadSocialRemoveKey(val): Observable<ISocialReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/removeSocialProfile/${val.socialKey}`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem loadSocialRemoveKey post " + e);
            }),
            map((v: ISocialReply) => {
                v.paramId = val.paramId;
                return v;
            }),
        );
    }

    /**
     * Removes ALL social media profiles for the current business account
     *
     * @description
     * Bulk removal operation that disconnects ALL connected social media accounts
     * for the current business/enterprise account. This is typically used when
     * transferring accounts or resetting social integrations.
     *
     * @returns Observable<any> - Confirmation with count of removed profiles
     *
     * @remarks
     * **DANGEROUS OPERATION:**
     * - Removes ALL social profiles at once
     * - Cannot be undone - user must re-authorize each platform individually
     * - Recommended to confirm with user before calling
     * - Only affects current business ID (from authenticated user)
     *
     * **Use Cases:**
     * - Account cleanup/reset
     * - Business transfer/migration
     * - Security breach response
     * - Bulk disconnection for testing
     *
     * @example
     * ```typescript
     * // With confirmation dialog
     * if (confirm('Remove ALL social media connections? This cannot be undone.')) {
     *     this.userService.loadSocialRemoveKeyWithBusinessId()
     *         .subscribe(response => {
     *             console.log(`Removed ${response.removedCount} social profiles`);
     *         });
     * }
     * ```
     */
    public loadSocialRemoveKeyWithBusinessId(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/removeSocialProfileWithBusinessId`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem loadSocialRemoveKey post " + e);
            }),
            map((v: any) => v),
        );
    }

    // ============================================================================
    // SECTION: Global Configuration & User Preferences
    // ============================================================================

    /**
     * Saves global SignStudio configuration settings to server for cloud sync
     *
     * @description
     * Persists user preferences and application settings to the cloud, enabling
     * settings synchronization across devices and sessions. This includes UI preferences,
     * editor settings, default values, and feature flags.
     *
     * @param localStorageConfig - Configuration object with user preferences
     *
     * @returns Observable<any> - Save confirmation with synced config
     *
     * @remarks
     * **Synced Settings Include:**
     * - Editor preferences (grid snap, rulers, guides)
     * - UI theme and color scheme
     * - Default scene settings (resolution, duration)
     * - Resource library sorting/filtering
     * - Recent file lists
     * - Feature toggles and beta flags
     * - Keyboard shortcut customizations
     * - Language/locale preferences
     *
     * **Sync Behavior:**
     * - Automatically merges with existing cloud settings
     * - Conflicts resolved with "last write wins" strategy
     * - Settings available on all devices after login
     * - Cached locally for offline access
     *
     * **Returns Empty Observable If:**
     * - User is not authenticated
     * - Offline mode is enabled
     *
     * @example
     * ```typescript
     * const config = {
     *     theme: 'dark',
     *     editorGridSnap: true,
     *     defaultSceneResolution: '1920x1080',
     *     recentFiles: [...],
     *     customShortcuts: {...}
     * };
     * this.userService.dwSignSetGlobalConfig(config)
     *     .subscribe(response => {
     *         console.log('Settings synced to cloud');
     *     });
     * ```
     */
    public dwSignSetGlobalConfig(localStorageConfig, i_user?: IUser): Observable<any> {
        /** i_user: credentials captured at dispatch time (logout pushes the config and then clears the user);
         *  after logout the store holds an empty user object, so guard on username rather than the object **/
        const user: IUser = i_user || this.m_user;
        if (isNullOrUndefOrEmpty(user?.username))
            return EMPTY;
        const header = getCredentialsHeader(user);
        const url = `${this.appSettings.globalService}/dwSignSetGlobalConfig`;
        return this._http.put<any>(url, localStorageConfig, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem dwSignSetGlobalConfig post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwDallImage(v): Observable<any> {
        const header = getCredentialsHeaderImage(this.m_user);
        const url = `${this.appSettings.globalService}/dwDallGetImage`;
        return this._http.put<any>(url, { url: v.prompt }, header).pipe(
            catchError((e) => {
                return of("problem dwDallImage post " + e);
            }),
            map((val) => val),
        );
    }

    // ============================================================================
    // SECTION: AI Services (Image Generation, Video Generation, Text-to-Speech)
    // ============================================================================

    /**
     * Generates AI images using DALL-E or Google Veo with configurable resolution
     *
     * @description
     * Creates AI-generated images from text prompts using OpenAI's DALL-E API. Supports
     * both high and low resolution outputs. The method displays a toast notification to
     * inform users of the processing status and includes retry logic for reliability.
     *
     * @param prompt - Prompt configuration object
     * @param prompt.text - Text description of the desired image
     * @param prompt.resolution - Image quality ('high' or 'low', default: 'high')
     *
     * @returns Observable<any> - Response containing generated image URL and metadata
     *
     * @remarks
     * **Resolution Options:**
     * - **high**: Uses `/dwDallImageV2` endpoint for high-quality 1024x1024 images
     * - **low**: Uses `/dwDallImageV2_low` endpoint for lower resolution/faster generation
     *
     * **Features:**
     * - Automatic user notification via toast
     * - Retry logic with exponential backoff
     * - Requires valid user credentials
     * - Integration with OpenAI DALL-E API via mediaCLOUD proxy
     *
     * @example
     * ```typescript
     * // Generate high-resolution image
     * const prompt = {
     *   text: 'A futuristic cityscape at sunset with flying cars',
     *   resolution: 'high'
     * };
     * this.userService.aiImages(prompt)
     *   .subscribe(response => {
     *     console.log('Generated image URL:', response.url);
     *     // Use the image URL to display or save
     *   });
     *
     * // Generate low-resolution image for faster results
     * const quickPrompt = {
     *   text: 'A simple logo design',
     *   resolution: 'low'
     * };
     * this.userService.aiImages(quickPrompt)
     *   .subscribe(response => {
     *     console.log('Quick image URL:', response.url);
     *   });
     * ```
     */
    public aiImages(prompt): Observable<any> {
        const header = getCredentialsHeader(this.m_user);

        // Randomly select between ChatGPT and Google Veo
        // const useGoogleVeo = Math.random() < 0.5;
        // const url = useGoogleVeo
        //     ? `${this.appSettings.globalService}/dwVeoImage`
        //     : `${this.appSettings.globalService}/dwDallImageV2`;
        //
        // Show toast message to user
        // const toastMessage = useGoogleVeo
        //     ? 'Sending command via Google Veo'
        //     : 'Sending command via ChatGPT';

        const urlLow = `${this.appSettings.globalService}/dwDallImageV2_low`;
        const urlHigh = `${this.appSettings.globalService}/dwDallImageV2`;

        // Check if resolution is specified in the prompt object
        const resolution = prompt?.resolution || "high";
        const selectedUrl = resolution === "low" ? urlLow : urlHigh;

        this.toast.info("producing AI image, please wait...");
        return this._http.put<any>(selectedUrl, prompt, header).pipe(
            retryWithBackoff(selectedUrl, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem dwDallImages post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Generates AI images using the unified V5 API (GPT-Image 1.5/1/Mini + DALL-E)
     *
     * @description
     * Creates AI-generated images using OpenAI's latest image generation API. Supports
     * multiple operations: generate (text-to-image), edit (modify existing images),
     * and variation (create variations of existing images).
     *
     * @param request - V5 API request configuration
     * @param request.operation - Operation type: 'generate', 'edit', or 'variation'
     * @param request.model - AI model to use (default: 'gpt-image-1.5')
     * @param request.prompt - Text description (required for generate/edit)
     * @param request.image - Base64 or URL of image (required for edit/variation)
     * @param request.mask - Optional mask for edit operations
     * @param request.n - Number of images to generate (1-10)
     * @param request.size - Image dimensions
     * @param request.quality - Quality level: 'low', 'medium', 'high'
     *
     * @returns Observable<any> - Response containing generated image URLs and usage stats
     *
     * @remarks
     * **Available Models:**
     * - gpt-image-2: Top quality, best text rendering, up to 2K resolution, non-Latin scripts (released 2026-04-22)
     * - gpt-image-1.5: 4x faster, best quality (default)
     * - gpt-image-1: Fast, high quality
     * - gpt-image-1-mini: Fastest, good for drafts
     * - dall-e-3: Medium speed, excellent quality
     * - dall-e-2: Fast, good for variations only
     *
     * **Size Options by Model:**
     * - GPT-Image 1.5/1/Mini: 1024x1024, 1536x1024, 1024x1536, auto
     * - DALL-E 3: 1024x1024, 1792x1024, 1024x1792
     * - DALL-E 2: 256x256, 512x512, 1024x1024
     *
     * @example
     * ```typescript
     * // Generate a new image
     * const request = {
     *   operation: 'generate',
     *   model: 'gpt-image-1.5',
     *   prompt: 'A futuristic cityscape at sunset',
     *   size: '1024x1024',
     *   quality: 'high'
     * };
     * this.userService.aiImagesV5(request).subscribe(response => {
     *   console.log('Generated images:', response.data);
     * });
     *
     * // Edit an existing image
     * const editRequest = {
     *   operation: 'edit',
     *   model: 'gpt-image-1.5',
     *   prompt: 'Add a rainbow in the sky',
     *   image: 'data:image/png;base64,...',
     *   size: '1024x1024'
     * };
     * this.userService.aiImagesV5(editRequest).subscribe(response => {
     *   console.log('Edited image:', response.data);
     * });
     * ```
     */
    public aiImagesV5(request: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwDallImageV5`;

        // Show appropriate toast message based on operation
        const operationMessages = {
            generate: "generating AI image please wait",
            edit: "editing AI image please wait",
            variation: "creating image variation please wait",
        };
        const message = operationMessages[request.operation] || "processing AI image please wait";
        this.toast.info(message);

        return this._http.put<any>(url, request, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ error: "problem with AI image request " + e.message });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Generates AI video using Google Veo video synthesis
     *
     * @description
     * Creates AI-generated video content from text prompts using Google's Veo video
     * generation model. Supports development mode for testing without consuming quota.
     *
     * @param i_prompt - Text description of the desired video content
     *
     * @returns Observable<any> - Response containing generated video URL and metadata
     *
     * @remarks
     * - Uses Google Veo AI model for video generation
     * - Automatically detects dev mode for testing
     * - Requires valid user credentials
     * - Includes retry logic for network resilience
     *
     * @example
     * ```typescript
     * const prompt = 'A serene lake at sunset with mountains in the background';
     * this.userService.dwVeoVideo(prompt)
     *   .subscribe(response => {
     *     console.log('Generated video URL:', response.videoUrl);
     *   });
     * ```
     */
    public dwVeoVideo(i_prompt: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwVeoVideo`;
        const inDevMode = isHostLocal();
        // const inDevMode = false;
        return this._http.put<any>(url, { message: i_prompt, dev: inDevMode }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem dwDallImages post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Generates AI video using Runway ML video synthesis with configurable options
     *
     * @description
     * Creates AI-generated video content using Runway's video generation models.
     * Supports multiple models, durations, and resolutions with a user-friendly
     * toast notification during processing.
     *
     * @param options - Video generation configuration
     * @param options.prompt - Text description of the desired video
     * @param options.model - AI model to use (default: 'veo3.1_fast')
     * @param options.duration - Video length in seconds (default: 4)
     * @param options.resolution - Video dimensions (default: '1280:720')
     *
     * @returns Observable<any> - Response containing task ID for polling status
     *
     * @remarks
     * **Available Models:**
     * - veo3.1_fast: Fast generation, good quality
     * - veo3.1: Higher quality, slower generation
     *
     * **Common Resolutions:**
     * - '1280:720' (HD 720p)
     * - '1920:1080' (Full HD 1080p)
     * - '640:360' (Lower quality, faster)
     *
     * @example
     * ```typescript
     * const options = {
     *   prompt: 'A bird flying over the ocean',
     *   model: 'veo3.1_fast',
     *   duration: 4,
     *   resolution: '1280:720'
     * };
     * this.userService.dwRunawayVideo(options)
     *   .subscribe(response => {
     *     const taskId = response.taskId;
     *     // Poll status with dwRunwayVideoStatus(taskId)
     *   });
     * ```
     */
    public dwRunawayVideo(options: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwRunwayVideo`;
        this.toast.info("producing AI video, please wait...");

        const payload = {
            prompt: options.prompt,
            model: options.model || "gen4.5",
            duration: options.duration || 5,
            resolution: options.resolution || "1280:720",
        };

        return this._http.put<any>(url, payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem dwRunwayVideo post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Polls the status of a Runway video generation task
     *
     * @description
     * Checks the progress/status of an AI video generation task initiated with dwRunawayVideo().
     * Used for polling until the video generation is complete.
     *
     * @param taskId - Task identifier returned from dwRunawayVideo()
     *
     * @returns Observable<any> - Status response with progress and video URL when complete
     *
     * @remarks
     * - Poll this endpoint periodically (e.g., every 2-5 seconds)
     * - Returns progress percentage and status
     * - Final response includes video download URL
     * - Errors are returned in the response object
     *
     * @example
     * ```typescript
     * // After initiating video generation
     * const taskId = 'task_abc123';
     *
     * // Poll every 3 seconds until complete
     * const pollInterval = interval(3000).pipe(
     *   switchMap(() => this.userService.dwRunwayVideoStatus(taskId)),
     *   takeWhile(status => status.status !== 'completed', true)
     * );
     *
     * pollInterval.subscribe(status => {
     *   console.log('Progress:', status.progress);
     *   if (status.status === 'completed') {
     *     console.log('Video ready:', status.videoUrl);
     *   }
     * });
     * ```
     */
    public dwRunwayVideoStatus(taskId: string): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwRunwayVideoStatus`;

        const payload = {
            taskId: taskId,
        };

        return this._http.put<any>(url, payload, header).pipe(
            catchError((e) => {
                console.error("Error polling video status:", e);
                return of({ error: "Failed to poll video status", details: e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Creates an AI-generated video using Remotion from a text prompt
     *
     * @description
     * Spawns a background video generation process on the server using Claude AI
     * for scene configuration and Remotion for rendering. Returns immediately with
     * a taskId for polling progress via dwRemotionVideoStatus().
     *
     * @param options - Video generation configuration
     * @param options.prompt - Text description of the desired video
     * @param options.images - Optional array of image URLs to include
     * @param options.videos - Optional array of video URLs to include
     *
     * @returns Observable with taskId, status, and usage info
     *
     * @example
     * ```typescript
     * this.userService.dwRemotionVideo({
     *   prompt: 'A 15 second bakery promo with warm colors',
     *   images: ['https://s3.amazonaws.com/bucket/bread.jpg']
     * }).subscribe(response => {
     *   const taskId = response.taskId;
     *   // Poll status with dwRemotionVideoStatus(taskId)
     * });
     * ```
     */
    public dwRemotionVideo(options: { prompt: string; images?: string[]; videos?: string[]; engine?: 'claude' | 'gemma' }): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwRemotionVideo`;
        this.toast.info("producing AI video, please wait...");

        return this._http.put<any>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ error: "problem dwRemotionVideo request " + e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Polls the status of a Remotion video generation task
     *
     * @param taskId - Task identifier returned from dwRemotionVideo()
     * @returns Observable with status, progress, message, and output when complete
     *
     * @example
     * ```typescript
     * const pollInterval = interval(3000).pipe(
     *   switchMap(() => this.userService.dwRemotionVideoStatus(taskId)),
     *   takeWhile(status => status.status !== 'completed', true)
     * );
     * ```
     */
    public dwRemotionVideoStatus(taskId: string): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwRemotionVideoStatus`;

        return this._http.put<any>(url, { taskId }, header).pipe(
            catchError((e) => {
                console.error("Error polling Remotion video status:", e);
                return of({ error: "Failed to poll video status", details: e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Edits an existing Remotion video configuration with a new instruction
     *
     * @param options - Edit configuration
     * @param options.taskId - Existing task ID to edit
     * @param options.prompt - Edit instruction describing desired changes
     * @param options.images - Optional replacement image URLs
     * @param options.videos - Optional replacement video URLs
     *
     * @returns Observable with taskId, status, and usage info
     *
     * @example
     * ```typescript
     * this.userService.dwRemotionVideoEdit({
     *   taskId: 'abc123',
     *   prompt: 'Change colors to blue and make it 30 seconds'
     * }).subscribe(response => {
     *   // Poll status with dwRemotionVideoStatus(response.taskId)
     * });
     * ```
     */
    public dwRemotionVideoEdit(options: { taskId: string; prompt: string; images?: string[]; videos?: string[] }): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwRemotionVideoEdit`;
        this.toast.info("editing AI video, please wait...");

        return this._http.put<any>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ error: "problem dwRemotionVideoEdit request " + e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Retrieves global SignStudio configuration settings from cloud storage
     *
     * @description
     * Fetches user preferences and application settings from the cloud, enabling
     * settings synchronization across devices and browser sessions. This is the
     * counterpart to dwSignSetGlobalConfig() and is typically called on app startup.
     *
     * @param localStorageConfig - Query parameters for filtering config retrieval
     *
     * @returns Observable<any> - Cloud-stored configuration object
     *
     * @remarks
     * **Retrieved Settings Include:**
     * - Editor preferences (grid snap, rulers, guides)
     * - UI theme and color scheme
     * - Default scene settings (resolution, duration, fps)
     * - Resource library view preferences
     * - Recent file/project history
     * - Feature flags and beta features
     * - Custom keyboard shortcuts
     * - Language/locale preferences
     * - Last opened project/scene
     *
     * **Typical Usage Flow:**
     * 1. User logs in
     * 2. App calls dwSignGetGlobalConfig()
     * 3. Retrieved config merged with local defaults
     * 4. Settings applied to UI
     * 5. User makes changes
     * 6. Changes saved with dwSignSetGlobalConfig()
     *
     * **Fallback Behavior:**
     * - Returns empty config if no cloud settings exist (new user)
     * - Local defaults used when cloud config is missing
     *
     * @example
     * ```typescript
     * // On app startup
     * this.userService.dwSignGetGlobalConfig({})
     *     .subscribe(cloudConfig => {
     *         const mergedConfig = { ...defaultConfig, ...cloudConfig };
     *         this.applySettings(mergedConfig);
     *         console.log('Applied theme:', mergedConfig.theme);
     *     });
     * ```
     */
    public dwSignGetGlobalConfig(localStorageConfig): Observable<any> {
        if (isNullOrUndefOrEmpty(this.m_user?.username))
            return EMPTY;
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSignGetGlobalConfig`;
        /** the server keys the lookup off the Authorization header only, never send the user object as a body **/
        return this._http.put<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of("problem dwSignGetGlobalConfig post " + e);
            }),
            map((v: any) => v),
        );
    }

    // ============================================================================
    // SECTION: DynaWebs Chat & Analytics Services
    // ============================================================================

    /**
     * Retrieves chat statistics and analytics for a DynaWebs order
     *
     * @description
     * Fetches comprehensive chat metrics for a specific DynaWebs order including
     * message counts, active conversations, response times, customer satisfaction,
     * and agent performance statistics.
     *
     * @param orderId - DynaWebs order ID to fetch chat statistics for
     *
     * @returns Observable<any> - Chat statistics and analytics data
     *
     * @remarks
     * **Statistics Include:**
     * - Total messages sent/received
     * - Active conversations count
     * - Average response time
     * - Customer satisfaction scores
     * - Agent online/offline status
     * - Peak usage hours
     * - Conversation duration metrics
     * - Unread/unanswered message counts
     *
     * **Use Cases:**
     * - Dashboard analytics displays
     * - Performance monitoring
     * - Customer service reports
     * - Real-time chat status
     * - Historical trend analysis
     *
     * **Data Retention:**
     * - Real-time stats for active chats
     * - Historical data typically retained 90 days
     *
     * @example
     * ```typescript
     * const orderId = 'order_12345';
     * this.userService.dwChatStats(orderId)
     *     .subscribe(stats => {
     *         console.log('Total messages:', stats.totalMessages);
     *         console.log('Active chats:', stats.activeConversations);
     *         console.log('Avg response time:', stats.avgResponseTime);
     *         console.log('Satisfaction:', stats.satisfactionScore);
     *     });
     * ```
     */
    public dwChatStats(orderId): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwChatStats`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwDeleteOrder post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Retrieves SignStudio usage statistics and analytics for a DynaWebs order
     *
     * @description
     * Fetches comprehensive usage metrics for SignStudio within a DynaWebs account,
     * including scene creation stats, player deployments, resource usage, feature
     * adoption rates, and overall platform engagement metrics.
     *
     * @param orderId - DynaWebs order ID to fetch SignStudio statistics for
     *
     * @returns Observable<any> - SignStudio usage statistics and analytics
     *
     * @remarks
     * **Statistics Include:**
     * - Total scenes created
     * - Active players/screens
     * - Resource library size and usage
     * - Scene playback duration
     * - Most used components/blocks
     * - Template usage frequency
     * - Content update frequency
     * - Feature adoption metrics
     * - Multi-screen deployment stats
     * - Schedule utilization
     *
     * **Use Cases:**
     * - Usage dashboards and reports
     * - Feature adoption tracking
     * - Content creation analytics
     * - Platform engagement metrics
     * - License compliance monitoring
     * - Customer success insights
     *
     * **Update Frequency:**
     * - Stats updated in real-time for active sessions
     * - Aggregated stats updated hourly
     * - Historical trends available for 90 days
     *
     * @example
     * ```typescript
     * const orderId = 'order_12345';
     * this.userService.dwSignStudioStats(orderId)
     *     .subscribe(stats => {
     *         console.log('Total scenes:', stats.totalScenes);
     *         console.log('Active players:', stats.activePlayers);
     *         console.log('Resources used:', stats.resourceCount);
     *         console.log('Most popular block:', stats.topComponent);
     *     });
     * ```
     */
    public dwSignStudioStats(orderId): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSignStudioStats`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwDeleteOrder post " + e);
            }),
            map((v: any) => v),
        );
    }

    // ============================================================================
    // SECTION: Reseller & White Label Configuration
    // ============================================================================

    /**
     * Fetches reseller/white label configuration BEFORE user login
     *
     * @description
     * Retrieves reseller branding and configuration for the login page and pre-auth
     * UI. This enables white-label partners to display their branding, custom themes,
     * and configuration before users authenticate. XML studio configuration is
     * automatically parsed into JSON.
     *
     * @param resellerId - Reseller ID to fetch configuration for (e.g., '1', '100', '575579')
     *
     * @returns Observable<IPreloadResellerInfo> - Reseller configuration with parsed studio settings
     *
     * @remarks
     * **Configuration Includes:**
     * - Company name and branding
     * - Custom logo URLs
     * - Theme colors and styling
     * - Custom studio XML configuration (auto-parsed to JSON)
     * - Feature flags and restrictions
     * - Support contact information
     * - Custom domain settings
     * - License tier information
     *
     * **XML Parsing:**
     * - Automatically parses `custom_studio` XML to `custom_studio_json`
     * - Returns null if no custom studio config exists
     * - Parsing errors logged to console
     *
     * **Common Reseller IDs:**
     * - 1: Default SignStudio branding
     * - 100: Generic white label
     * - Custom: Partner-specific branding
     *
     * **Timing:**
     * - Called on app load BEFORE login screen
     * - Determines login page appearance
     * - Cached for session duration
     *
     * @example
     * ```typescript
     * // On app initialization
     * const resellerId = '575579'; // From subdomain or config
     * this.userService.dwPreloginGetResellerInfo(resellerId)
     *     .subscribe(resellerInfo => {
     *         if (resellerInfo) {
     *             this.applyBranding(resellerInfo.custom_studio_json);
     *             this.setLogo(resellerInfo.logoUrl);
     *             this.setTheme(resellerInfo.themeColors);
     *         }
     *     });
     * ```
     */
    dwPreloginGetResellerInfo(resellerId): Observable<IPreloadResellerInfo> {
        const url = `${this.appSettings.globalService}/dwPreloginGetResellerInfo/${resellerId}`;
        return this._http.get<any>(url, httpOptionsJson).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((preloadResellerInfo: IPreloadResellerInfo) => {
                if (!preloadResellerInfo?.custom_studio) return null;
                xml2js.parseString(preloadResellerInfo.custom_studio, (err, jsonCustomStudio) => {
                    if (err) return console.log("problem parsing prelogin studio XML");
                    preloadResellerInfo.custom_studio_json = jsonCustomStudio?.Studio;
                });
                return preloadResellerInfo;
            }),
        );
    }

    /** previous version which did not create / create payer id, so until we released we will use getBusinessUserInfoV3, later Alon needs to add payer id creat **/
    // getBusinessUserInfoV2(i_user, i_pass): Observable<IAccountReplyV2> {
    //     const wait = Lib.DevMode() ? 500 : 1500;
    //     const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=GetBusinessUserInfo&userName=${i_user}&password=${i_pass}&ver=2`;
    //     return this._http.jsonp<any>(url, 'callback').pipe(
    //         retryWithBackoff(url, this.storeSelector, this.toast),
    //         delay(wait),
    //         map((v: IAccountReplyV2) => {
    //             if (v.result) {
    //                 return v;
    //             } else {
    //                 const privilegeInfoXml = Lib.Base64().decode(v.businessUserInfo.privilegeInfo);
    //                 // const customStudio = Lib.Base64().decode(v.businessUserInfo.resellerInfo.customStudio);
    //                 // const privilegeId = v.businessUserInfo.privilegeId;
    //                 // const accessMask = v.businessUserInfo.accessMask;
    //                 if (v.businessUserInfo.resellerInfo && v.businessUserInfo.resellerInfo.customStudio) {
    //                     const playerData = Lib.Base64().decode(v.businessUserInfo.resellerInfo.customStudio);
    //                     xml2js.parseString(playerData, (err, resellerXml) => {
    //                         if (err) {
    //                             console.error('problem decoding enterprise studio xml');
    //                             this.toast.error(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
    //                             this.storeSelector.sendEmail(ADMINISTRATOR, 'error in priv json', this?.m_user?.email, true);
    //                             return throwError(err + ' problem decoding enterprise studio xml');
    //                         }
    //                         v.businessUserInfo.resellerInfo.customStudio = resellerXml;
    //                         if (privilegeInfoXml) {
    //                             xml2js.parseString(privilegeInfoXml, (err2, privilegeInfo) => {
    //                                 if (err2) {
    //                                     console.error('problem decoding enterprise studio privilegeInfo xml');
    //                                     this.storeSelector.sendEmail(ADMINISTRATOR, 'error in priv json', this?.m_user?.email, true);
    //                                     this.toast.error(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
    //                                     return throwError(err + ' problem decoding enterprise studio privilegeInfo xml');
    //                                 }
    //                                 v.businessUserInfo.privilegeInfo = privilegeInfo;
    //                             });
    //                         }
    //
    //                     });
    //                 }
    //                 return v;
    //             }
    //         })
    //     );
    // }

    // createAccountV3(options: IAccountRequest): Observable<any> {
    //     const url = `${this.appSettings.globalService}/createAccountV3`;
    //     const header = {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'json' as 'json'};
    //     return this._http.put<IAccountReplyV2>(url, options, header).pipe(
    //         retryWithBackoff(url, this.storeSelector, this.toast),
    //         catchError((e) => {
    //             this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
    //             return of('problem saveCustomerDynawebsInfo post ' + e);
    //         }),
    //         map((v: any) => v)
    //     );
    // }

    // ============================================================================
    // SECTION: Enterprise Authentication (LDAP/Active Directory)
    // ============================================================================

    /**
     * Authenticates user via LDAP/Active Directory for enterprise single sign-on
     *
     * @description
     * Performs enterprise authentication using LDAP (Lightweight Directory Access Protocol)
     * or Active Directory credentials. This enables organizations to use their existing
     * directory services for SignStudio authentication, providing centralized user
     * management and single sign-on (SSO) capabilities.
     *
     * @param options - LDAP authentication request object
     * @param options.createCustomerAccount - Account details object
     * @param options.createCustomerAccount.userName - LDAP username (e.g., 'user@domain.com' or 'DOMAIN\\user')
     * @param options.createCustomerAccount.password - LDAP password
     * @param options.createCustomerAccount.domain - Optional AD domain (e.g., 'COMPANY')
     *
     * @returns Observable<IAccountReplyV2> - Authentication response with user info and business data
     *
     * @remarks
     * **LDAP Configuration Requirements:**
     * - LDAP server must be configured in DynaWebs settings
     * - LDAP bind credentials configured server-side
     * - SSL/TLS recommended for secure authentication
     * - Firewall rules allowing LDAP traffic (port 389 or 636 for LDAPS)
     *
     * **Supported Directory Services:**
     * - Microsoft Active Directory
     * - OpenLDAP
     * - Apache Directory Server
     * - Oracle Internet Directory
     * - IBM Security Directory Server
     *
     * **Authentication Flow:**
     * 1. User enters LDAP credentials
     * 2. SignStudio sends credentials to server
     * 3. Server performs LDAP bind operation
     * 4. On success, user account provisioned/updated
     * 5. Session token returned to client
     * 6. User logged in to SignStudio
     *
     * **Username Formats:**
     * - UPN: user@company.com
     * - Down-level: COMPANY\user
     * - DN: cn=user,ou=users,dc=company,dc=com
     *
     * **Security Features:**
     * - Credentials never stored in SignStudio
     * - Password validated against directory only
     * - Account auto-provisioned on first login
     * - Group memberships can map to SignStudio roles
     *
     * **Response Delay:**
     * - 500ms in dev mode for testing
     * - 1500ms in production to prevent brute force
     *
     * @example
     * ```typescript
     * // Active Directory authentication
     * const ldapAuth: IAccountRequest = {
     *     createCustomerAccount: {
     *         userName: 'jdoe@company.com',
     *         password: 'userPassword123',
     *         domain: 'COMPANY'
     *     }
     * };
     * this.userService.dwLdapAuth(ldapAuth)
     *     .subscribe(
     *         response => {
     *             if (response.result === 0) {
     *                 console.log('LDAP auth successful');
     *                 console.log('User:', response.businessUserInfo);
     *                 // Proceed to load user data
     *             } else {
     *                 console.error('LDAP auth failed:', response.error);
     *             }
     *         },
     *         error => {
     *             console.error('LDAP server unreachable:', error);
     *         }
     *     );
     * ```
     *
     * @example
     * ```typescript
     * // OpenLDAP authentication
     * const ldapAuth: IAccountRequest = {
     *     createCustomerAccount: {
     *         userName: 'cn=john.doe,ou=users,dc=example,dc=com',
     *         password: 'userPassword'
     *     }
     * };
     * this.userService.dwLdapAuth(ldapAuth).subscribe(response => {
     *     // Handle authentication
     * });
     * ```
     */
    dwLdapAuth(options: IAccountRequest): Observable<IAccountReplyV2> {
        const header = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.globalService}/ldapAuth`;
        return this._http.put<IAccountRequest>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IAccountReplyV2) => {
                return v;
            }),
        );
    }

    /* version 1 Kayako */
    liveChatStatus(): Observable<any> {
        const header = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.globalService}/dwGetLiveChat`;
        return this._http.get<any>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: any) => {
                return v.liveChatStatus;
            }),
        );
    }

    /* version 2 DynaWebs */
    liveChatStatusV2(): Observable<any> {
        const header = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.globalService}/chatSupport/1`;
        return this._http.get<any>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IChatServerReply) => {
                return v;
            }),
        );
    }

    createAccount(accountRequest: IAccountRequest): Observable<IAccountReplyV2> {
        /**
         // enable this line if in dev mode wish to create a fake account sherwin3@ms.com ...
         const v: IAccountReplyV2 = {result: 'pass', businessId: '391067'};
         return new Observable(subscriber => {
         setTimeout(() => {
         subscriber.next(v);
         subscriber.complete();
         }, 3000);
         });
         **/
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=CreateCustomerAccount&businessName=${accountRequest.createCustomerAccount.businessName}&userName=${accountRequest.createCustomerAccount.userName}&password=${Lib.Base64().encode(accountRequest.createCustomerAccount.password)}&templateBusinessId=${accountRequest.createCustomerAccount.templateBusinessId}&resellerId=${accountRequest.createCustomerAccount.resellerId}&firstName=${accountRequest.createCustomerAccount.firstName}&lastName=${accountRequest.createCustomerAccount.lastName}&contactEmail=${accountRequest.createCustomerAccount.contactEmail}&workPhone=${accountRequest.createCustomerAccount.workPhone}&cellPhone=${accountRequest.createCustomerAccount.cellPhone}&address=${accountRequest.createCustomerAccount.address}&city=${accountRequest.createCustomerAccount.city}&state=${accountRequest.createCustomerAccount.state}&contry=${accountRequest.createCustomerAccount.contry}&zipcode=${accountRequest.createCustomerAccount.zipcode}&studioType=${accountRequest.createCustomerAccount.studioType}&&emailUpdates=${accountRequest.createCustomerAccount.emailUpdates}&base64=1`;
        return this._http.jsonp<any[]>(url, "callback").pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IAccountReplyV2) => {
                return v;
            }),
        );
    }

    /**
     * Fire-and-forget: persist first-touch attribution (utm/referrer captured into
     * sessionStorage by main.ts from decorated getstarted links) for a newly created
     * account. Must never block or fail the signup flow.
     */
    reportAccountAttribution(businessId: string): void {
        try {
            const raw = sessionStorage.getItem("ds_attrib");
            if (!raw || !businessId) return;
            const url = `${this.appSettings.globalService}/dwAccountAttribution`;
            this._http.post(url, {businessId, ...JSON.parse(raw)}).subscribe({next: () => {}, error: () => {}});
        } catch (e) {
            // attribution reporting is best-effort only
        }
    }

    getAnnualPayData(payload): Observable<IAnnualPayData> {
        const url = `https://pay.signage.me/getPaymentInfo.ashx?args=${payload.paymentId}`;
        return this._http.post(url, {}).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IAnnualPayData) => {
                return v;
            }),
        );
    }

    updateAnnualPayData(v: any): Observable<IAnnualPayData> {
        const url = `https://pay.signage.me/payPendingPayment.ashx?args=${v.paymentId}`;
        return this._http.post(url, v.payload).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IAnnualPayData) => {
                return v;
            }),
        );
    }

    resetPassword(accountRequest: IAccountRequest): Observable<IAccountReplyV2> {
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=ResetPassword&userName=${accountRequest.createCustomerAccount.userName}`;
        return this._http.jsonp<any[]>(url, "callback").pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IAccountReplyV2) => {
                return v;
            }),
        );
    }

    changePassword(accountRequest: IAccountRequest): Observable<IAccountReplyV2> {
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=ChangePassword&userName=${accountRequest.createCustomerAccount.userName}&oldPassword=${Lib.Base64().encode(accountRequest.createCustomerAccount.password)}&newPassword=${Lib.Base64().encode(accountRequest.createCustomerAccount.newPass)}&base64=1`;
        return this._http.jsonp<any[]>(url, "callback").pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IAccountReplyV2) => {
                return v;
            }),
        );
    }

    changeBusinessName(accountRequest: IAccountRequest): Observable<IAccountReplyV2> {
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=ChangeBusinessName&userName=${accountRequest.createCustomerAccount.userName}&password=${Lib.Base64().encode(accountRequest.createCustomerAccount.password)}&newBusnessName=${accountRequest.createCustomerAccount.businessName}&base64=1`;
        return this._http.jsonp<any[]>(url, "callback").pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IAccountReplyV2) => {
                return v;
            }),
        );
    }

    sendEmail(options: IEmailSend): Observable<IEmailSend> {
        const header = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "text" as "json",
        };
        const url = `${this.appSettings.globalService}/sendEmailStudio`;
        return this._http.put<IEmailSend>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IEmailSend) => v),
        );
    }

    ldapAuth(options): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "text" as "json",
        };
        const url = `${this.appSettings.globalService}/sendEmailStudio`;
        return this._http.put<any>(url, options, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: any) => v),
        );
    }

    getAccountDetails(): Observable<IAccountDetails> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "text" as "json",
        };
        const url = `${this.m_user.webApiResellerBaseUrl}&command=GetAccountInfo`;
        let enterpriseReply;
        return this._http.get<IAccountDetails>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map(v => {
                xml2js.parseString(v, (err, userAccountDetails) => {
                    if (err) {
                        console.error("getUserAccountDetails xml conversion error");
                        this.storeSelector.sendEmail(ADMINISTRATOR, "error in account details json", this?.m_user?.email, true);
                        this.onError(this.translocoService.translate("toast.sorryEncounteredProblem", {}, this.translocoService.getActiveLang()));
                    }
                    // enterpriseReply = Object.assign(userAccountDetails, accountDetailsTemplate);
                    // let c = _.extend(userAccountDetails, accountDetailsTemplate);
                    // console.log(c);
                    enterpriseReply = userAccountDetails;
                });
                return enterpriseReply;
            }),
        );
    }

    getAccountDetailsBillingToken(v): Observable<IAccountDetails> {
        const textXml = {
            headers: new HttpHeaders({
                "Content-Type": "text/xml",
                "Accept": "text/xml",
            }), responseType: "text" as "json",
        };
        const url = `${this.appSettings.globalService}/getAccountInfo/${v.password}`;
        let enterpriseReply;
        return this._http.put<IOrderForm[]>(url, {}, textXml).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map(v => {
                xml2js.parseString(v, (err, userAccountDetails) => {
                    if (err) {
                        console.error("getUserAccountDetails xml conversion error");
                        this.storeSelector.sendEmail(ADMINISTRATOR, "error in account details json", this?.m_user?.email, true);
                        this.onError(this.translocoService.translate("toast.sorryEncounteredProblem", {}, this.translocoService.getActiveLang()));
                    }
                    enterpriseReply = userAccountDetails;
                });
                return enterpriseReply;
            }),
        );
    }

    getOrders(): Observable<IOrderForm[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/orders_v2/${this.m_user.enterprise ? "ENTERPRISE" : "USER"}`;
        return this._http.put<IOrderForm[]>(url, { accountVersion: Lib.GetAccountVersion(this.m_user) }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IOrderForm[]) => v),
        );

        // const url = `${this.appSettings.globalService}/orders/${Lib.EncryptValue(this.m_user.username)}/${Lib.EncryptValue(this.m_user.passwordNew)}/${this.m_user.enterprise ? 'ENTERPRISE' : 'USER'}/1`;
        // const url = `${this.appSettings.globalService}/orders/${this.m_user.username}/${this.m_user.passwordNew}/${this.m_user.enterprise ? 'ENTERPRISE' : 'USER'}`;
        // return this._http.get<IOrderForm[]>(url, httpOptionsJson).pipe(
        //     retryWithBackoff(url, this.storeSelector, this.toast),
        //     map((v: IOrderForm[]) => v)
        // );
    }

    public getOrder(orderId: Id): Observable<IOrderDetails> {
        // const orderId = order.order.order_id ? order.order.order_id : order.billing.payer_id;
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/order_v2/${orderId}/${this.m_user.enterprise ? "ENTERPRISE" : "USER"}`;

        const obs1$ = this._http.put(url, { accountVersion: Lib.GetAccountVersion(this.m_user) }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IOrderDetails) => v),
        );

        const obs2$ = this.fintechService.getOrderInfo(orderId);


        return combineLatest(obs1$, obs2$).pipe(
            map(([value1, value2]) => {
                if (value2.status == "succeeded") {
                    value1.billing.company = value2.contact_company;
                    value1.billing.vat = value2.contact_vat;
                    value1.billing.first_name = value2.contact_first_name;
                    value1.billing.last_name = value2.contact_last_name;
                    value1.billing.phone1 = value2.contact_phone1;
                    value1.billing.phone2 = value2.contact_phone2;
                    value1.billing.email = value2.contact_email;

                    value1.shipping.company = value2.shipping_company;
                    value1.shipping.first_name = value2.shipping_first_name;
                    value1.shipping.last_name = value2.shipping_last_name;
                    value1.shipping.address1 = value2.shipping_address1;
                    value1.shipping.address2 = value2.shipping_address2;
                    value1.shipping.city = value2.shipping_city;
                    value1.shipping.state = value2.shipping_state;
                    value1.shipping.country = value2.shipping_country;
                    value1.shipping.zip_code = value2.shipping_zip_code;
                    value1.shipping.phone1 = value2.shipping_phone1;
                    value1.shipping.phone2 = value2.shipping_phone2;
                    value1.shipping.email = value2.shipping_email;

                    value1.billing.card_number = value2.card_last4;
                    value1.billing.card_brand = value2.card_brand;
                    value1.billing.expiration_month = value2.card_expiration_month;
                    value1.billing.expiration_year = value2.card_expiration_year;
                    value1.billing.cardholder = value2.card_cardholder;
                    value1.billing.address1 = value2.card_address1;
                    value1.billing.address2 = value2.card_address2;
                    value1.billing.city = value2.card_city;
                    value1.billing.state = value2.card_state;
                    value1.billing.zip_code = value2.card_postal_code;
                    value1.billing.country = value2.card_country;
                }
                return value1;
            }),
        );


        // const url = `${this.appSettings.globalService}/order/${this.m_user.username}/${this.m_user.passwordNew}/${orderId}/${this.m_user.enterprise ? 'ENTERPRISE' : 'USER'}`;
        // return this._http.get<IOrderDetails>(url, httpOptionsJson).pipe(
        //     retryWithBackoff(url, this.storeSelector, this.toast),
        //     map((v: IOrderDetails) => v)
        // );
        //
        // var appdb: Map<string, any> = this.appStore.getState().appdb;
        // var url;
        // url = appdb.get('appBaseUrlCloud').replace('END_POINT', 'order') + `/${orderId}/${accountType}`;
        // this._http.get(url)
        //     .catch((err) => {
        //         bootbox.alert('Error getting order details');
        //         return Observable.throw(err);
        //     })
        //     .finally(() => {
        //     })
        //     .map((result: any) => {
        //         var order: any = result.json();
        //         var orderDetailModel: OrderDetailModel = new OrderDetailModel(order);
        //         dispatch(this.receivedOrder(orderDetailModel));
        //     }).subscribe();
    }

    // getAccountStaticTemplates(): Observable<Array<ISceneTemplateImport>> {
    //   let url;
    //   const templateData = staticAccountTemplates;
    //   this.appSettings.workOffline ? url = this.appSettings.apiOfflineUrl + 'samples.json' : url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/getResellerTemplates.ashx`;
    //   const sceneTemplates: ISceneTemplateImport[] = [];
    //   // tslint:disable-next-line
    //   for (let templateItem in templateData) {
    //     const item = templateData[templateItem];
    //     const sampleItem: ISceneTemplateImport = {
    //       studioLite: '1',
    //       templateBusinessId: item[0],
    //       name: item[3].name,
    //       video: null, // `${this.appSettings.cloudResources}/business1000/resources/samples/${templateBusinessId}.mp4`,
    //       icon: [
    //         `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/sign_html/${item[0]}_1.png`,
    //         `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/sign_html/${item[0]}_2.png`,
    //         `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/sign_html/${item[0]}_3.png`,
    //         `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/sign_html/${item[0]}_4.png`
    //       ],
    //       category: item[3].folder,
    //       preview: null
    //     };
    //     sceneTemplates.push(sampleItem);
    //   }
    //   return of(sceneTemplates);
    // }

    getTemplatesFromServer(): Observable<Array<ISceneTemplateImport>> {
        let url;
        this.appSettings.workOffline ? url = this.appSettings.apiOfflineUrl + "samples.json" : url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/getResellerTemplates.ashx`;
        // The template tables now live in a lazy chunk. forkJoin (not switchMap) so the chunk
        // download and the server request run in PARALLEL and the request is not delayed.
        const tables$ = from(Promise.all([loadAccountTemplates(), loadSceneTemplates()]));
        const templateData$ = (this.appSettings.workOffline ? this._http.get<any[]>(url) : this._http.jsonp<any[]>(url, "callback")).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
        );
        return forkJoin([tables$, templateData$]).pipe(
            map(([[{ staticAccountTemplates }, { sceneTemplates: sceneTemplateTable }], templateData]: [any, any]) =>
                this.buildTemplateList(templateData, staticAccountTemplates, sceneTemplateTable),
            ),
        );
    }

    private buildTemplateList(templateData: any, staticAccountTemplates: Record<string, any>, sceneTemplateTable: Record<string, any>): Array<ISceneTemplateImport> {
        const sceneTemplates: ISceneTemplateImport[] = [];
        // let totalLiteTemplates = 0;
        // tslint:disable-next-line
        for (let templateName in templateData.templates) {
            // const isFullFeatures = true;
            // if (isFullFeatures && template.lite === '1') {
            //   totalLiteTemplates++;
            //   template.businessId = '575579';
            //   template.name = 'SignStudio template';
            //   if (totalLiteTemplates > 1) continue;
            // }
            let newName;
            let isTouch = false;
            const template = templateData.templates[templateName];

            if (template.name.match("simple")) {
                newName = template.name.replace(/_simple/, "");
                template.name = newName + " (simple)";
            } else if (template.name.match("touch")) {
                newName = template.name.replace(/_touch/, "");
                template.name = newName + " (touch)";
                isTouch = true;
            }

            // if (i_templateStyle !== SceneTemplateEnum.ALL && template.lite !== i_templateStyle) continue;
            const templateBusinessId = template.businessId;
            const sampleItem: ISceneTemplateImport = {
                studioType: template.lite,
                templateBusinessId,
                name: template.name,
                video: `${this.appSettings.cloudResourcesGPU}/business1000/resources/samples/${templateBusinessId}.mp4`,
                icon: `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/lite_html/${templateBusinessId}.png`,
                preview: template.previewUrl,
                touch: isTouch,
            };
            sceneTemplates.push(sampleItem);
        }
        // tslint:disable-next-line
        for (let templateName in staticAccountTemplates) {
            const item = staticAccountTemplates[templateName];
            const templateBusinessId = item[0];
            const isTouch = templateName.includes("Touch");

            const sampleItem: ISceneTemplateImport = {
                studioType: EnumStudioType.SIGN_STUDIO,
                templateBusinessId: item[0] as any,
                name: item[3].name + (isTouch ? " (touch)" : ""),
                video: item[3]?.video ? item[3].video : null, // `${this.appSettings.cloudResources}/business1000/resources/samples/${templateBusinessId}.mp4`,
                icon: `assets/images/accountIcons/${templateBusinessId}.png`,
                snaps: this.getSignStudioTemplateLargeSnapshots(templateBusinessId, sceneTemplateTable),
                category: item[3].folder,
                preview: null,
                touch: isTouch,
            };
            sceneTemplates.push(sampleItem);
        }
        return sceneTemplates;
    }

    private getSignStudioTemplateLargeSnapshots(templateBusinessId, sceneTemplateTable: Record<string, any>): Array<string> {
        const templateSnaps = [];
        Object.keys(sceneTemplateTable).forEach((templateName) => {
            const template: ISceneImportTemplate = {};
            template[templateName] = sceneTemplateTable[templateName];
            const businessId = template[templateName][0];
            if (businessId === templateBusinessId) {
                templateSnaps.push(`${appData.cloudResourcesS3}/business1000/resources/scenes_snaps/${templateName}.png`);
            }
        });
        return templateSnaps;
    }

    public sendStationCommand(i_command, i_selectionModel: SelectionModel<IStationData>, i_filterBySceneId: string = null) {
        this.cod(
            from(i_selectionModel.selected)
                .pipe(
                    take(i_selectionModel.selected.length),
                    map((i_station) => {
                        if (i_filterBySceneId && !compareIds(i_filterBySceneId, i_station.props.player_data_id))
                            return;
                        const stationId = i_station.props.station_id;
                        let businessDomain = this.coreAppData[0].businessDomain;
                        if (isHostDevOrLocal() && businessDomain === "ida.signage.me") {
                            businessDomain = `${businessDomain}:${DEV_TEST_PORT}`;
                        }
                        this.storeSelector.getStation(i_station)
                            .pipe(switchMap((i_station2) => {
                                if (i_station2?.version === "2") {
                                    const segment = "Web";
                                    const url = `${this.coreAppData[1].protocol}${businessDomain}/${segment}/sendCommand.ashx?user=${this.coreAppData[0].email}&pass=${Lib.Base64().encode(this.coreAppData[0].passwordNew)}&stationId=${stationId}&base64=1`;
                                    Lib.DevShow(url);
                                    return this._http.post(url, {
                                        command: i_command,
                                    }).pipe(
                                        retryWithBackoff(url, this.storeSelector, this.toast),
                                        delay(_.random(1, 1000)),
                                        map((v) => v),
                                    );
                                } else {
                                    const segment = "WebService";
                                    const url = `${this.coreAppData[1].protocol}${businessDomain}/${segment}/sendCommand.ashx?i_user=${this.coreAppData[0].email}&i_password=${Lib.Base64().encode(this.coreAppData[0].passwordNew)}&i_stationId=${stationId}&i_command=${i_command}&i_param1='SignageStudioLite'&i_param2=''&base64=1`;
                                    Lib.DevShow(url);
                                    return this._http.jsonp<any>(url, "callback").pipe(
                                        retryWithBackoff(url, this.storeSelector, this.toast),
                                        delay(_.random(1, 1000)),
                                        map((v) => v),
                                    );
                                }
                            }))
                            .subscribe((res: any) => {
                                Lib.DevShow(`send command response ${res?.ret}`);
                            });
                    }),
                ).subscribe((v) => {
            }),
        );
    }

    /**
     * Dynawebs
     */

    public dwGetBusinessUsage(cartRequest: any): Observable<IBusinessUsage> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwBusinessUsage/${cartRequest.businessId}`;
        return this._http.get<IBusinessUsage>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IBusinessUsage) => {
                return v;
            }),
        );
    }

    public dwCartGetOrderId(options: ICartRequest): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCartGetOrderId`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwCartGetOrderId " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwTextToSpeechCreate(text: string): Observable<any> {
        // disable credentials test for Oscar to use as open public service
        // const header = getCredentialsHeader(this.m_user);
        const header = {};
        const url = `${this.appSettings.globalService}/dwTextToSpeechCreate`;
        return this._http.put<any>(url, text, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem saveCustomerDynawebsInfo post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwTextToSpeechUpload(text: string): Observable<any> {
        // disable credentials test for Oscar to use as open public service
        // const header = getCredentialsHeader(this.m_user);
        const header = {};
        const url = `${this.appSettings.globalService}/dwTextToSpeechUpload`;
        return this._http.put<any>(url, text, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem saveCustomerDynawebsInfo post " + e);
            }),
            map((v: any) => v),
        );
    }


    public dwSetStudioType(options: { businessId: number, studioType: number }): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSetStudioType`;
        return this._http.put<any>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of({status: 'fail', message: "problem dwSetStudioType post " + e});
            }),
            map((v: any) => v),
        );
    }

    // Revert a migrated account back to its pre-migration legacy Studio. Mirrors the migration's
    // own two-call orchestration (which hits BusinessWeb + MasterService directly, not the node):
    //   1. BusinessWeb migrateAccount.ashx?revert=1 — soft-deletes the migrated scenes and restores
    //      the repointed legacy stations (business DB),
    //   2. MasterService revertMigration.ashx — restores studio_type/studio_lite from the
    //      customers.migrated_* columns and clears the migration stamp (master DB), secured by the
    //      business web_token. The server also enforces the 15-day window.
    // Returns {status:'ok'} only when BOTH steps succeed.
    public dwRevertMigration(options: { businessId: number }): Observable<any> {
        const protocol = this.appSettings?.protocol;
        const masterServer = this.appSettings?.masterServer;
        let businessDomain = this.m_user?.businessDomain;
        const webToken = this.m_user?.webToken;
        const businessId = options.businessId;
        if (!protocol || !businessDomain || !masterServer || !webToken || businessId == null) {
            return of({status: 'fail', message: 'missing revert params (domain/master/token)'});
        }
        // honor the "use dev server port 9443" setting, same as the migration path
        if (isHostDevOrLocal() && DEV_TEST_PORT !== 443) {
            businessDomain = `${businessDomain}:${DEV_TEST_PORT}`;
        }
        const bwUrl = `${protocol}${businessDomain}/Web/migrateAccount.ashx?businessId=${businessId}&revert=1`;
        const masterUrl = `${protocol}${masterServer}/WebService/revertMigration.ashx?businessId=${businessId}&token=${encodeURIComponent(webToken)}`;

        // Per-request ceiling: the business-DB revert runs a locking transaction (SELECT ... FOR
        // UPDATE on globals + UPDATEs on the migrated rows). If another transaction holds one of
        // those locks it would otherwise hang forever, so cap each server call and surface a clear
        // failure instead of a spinner that never resolves.
        const REVERT_HTTP_TIMEOUT_MS = 40000;
        // 1) business-DB revert first; only restore the master customers row if it succeeds
        return this._http.post(bwUrl, null, {responseType: 'text'}).pipe(
            timeout(REVERT_HTTP_TIMEOUT_MS),
            switchMap((bwRes: any) => {
                // require the revert-specific marker, NOT just status:'ok' — the migrate preview
                // response also returns status:'ok', so an un-redeployed BusinessWeb (which falls
                // through to preview) must NOT read as a successful revert.
                let bwOk = false;
                try { bwOk = JSON.parse(bwRes)?.reverted === true; } catch { /* leave false */ }
                if (!bwOk) return of({status: 'fail', message: 'business revert did not run (deploy BusinessWeb?)', detail: bwRes});
                return this._http.post(masterUrl, null, {responseType: 'text'}).pipe(
                    timeout(REVERT_HTTP_TIMEOUT_MS),
                    map((mRes: any) => {
                        let mOk = false;
                        try { mOk = JSON.parse(mRes)?.status === true; } catch { /* leave false */ }
                        return mOk ? {status: 'ok'} : {status: 'fail', message: 'master revert failed', detail: mRes};
                    })
                );
            }),
            catchError((e) => {
                // a timeout surfaces as a TimeoutError — name it so the banner is actionable
                const isTimeout = e?.name === 'TimeoutError';
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of({status: 'fail', message: isTimeout
                    ? 'revert timed out — the server is not responding (a locked transaction may be blocking it)'
                    : 'problem dwRevertMigration ' + e});
            }),
        );
    }

    // Admin-wide trace of every account that has migrated (all customers, every reseller). Goes through
    // MasterWeb billingService.ashx (same endpoint the resellers page uses), so it inherits that handler's
    // admin business_id allowlist auth — the logged-in email+password must belong to a whitelisted admin
    // account. Returns {status:'ok', accounts:[...]} or {status:'fail', message}.
    public dwGetMigratedAccounts(): Observable<any> {
        const options = {
            command: 'getMigratedAccounts',
            billingUser: this.m_user.email,
            billingPass: this.m_user.passwordNew
        };
        const url = "https://galaxy.signage.me/Web/billingService.ashx";
        return this._http.post(url, options).pipe(
            map((res: any) => {
                if (res?.result && Array.isArray(res.data)) {
                    return {status: 'ok', accounts: res.data};
                }
                return {status: 'fail', message: res?.error || 'not authorized'};
            }),
            catchError((e) => {
                return of({status: 'fail', message: 'problem dwGetMigratedAccounts ' + e});
            }),
        );
    }

    public dwGetStoppedPlayers(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwGetStoppedPlayers`;
        return this._http.get<any>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({status: 'fail', message: 'dwGetStoppedPlayers error: ' + e})),
            map((v: any) => v),
        );
    }

    public dwSaveStoppedPlayers(ids: number[]): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSaveStoppedPlayers`;
        return this._http.post<any>(url, {ids}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({status: 'fail', message: 'dwSaveStoppedPlayers error: ' + e})),
            map((v: any) => v),
        );
    }

    public dwFreeLoadersRunSearch(max: number): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwFreeLoadersRunSearch`;
        return this._http.post<any>(url, {max}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({status: 'fail', message: 'dwFreeLoadersRunSearch error: ' + e})),
            map((v: any) => v),
        );
    }

    public dwFreeLoadersRunIP(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwFreeLoadersRunIP`;
        return this._http.post<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({status: 'fail', message: 'dwFreeLoadersRunIP error: ' + e})),
            map((v: any) => v),
        );
    }

    public dwFreeLoadersDomainRun(domain: string): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwFreeLoadersDomainRun`;
        return this._http.post<any>(url, {domain}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({status: 'fail', message: 'dwFreeLoadersDomainRun error: ' + e})),
            map((v: any) => v),
        );
    }

    public dwFreeLoadersStatus(runId: string): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwFreeLoadersStatus?runId=${encodeURIComponent(runId)}`;
        return this._http.get<any>(url, header).pipe(
            catchError((e) => of({status: 'fail', message: 'dwFreeLoadersStatus error: ' + e})),
            map((v: any) => v),
        );
    }

    public dwMoveAccount(options: { businessIds: number[], targetResellerId: number, dryRun: boolean }): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwMoveAccount`;
        return this._http.post<any>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of({status: 'fail', message: "problem dwMoveAccount post " + e});
            }),
            map((v: any) => v),
        );
    }


    public dwAwakeSet(value: number): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/awake`;
        return this._http.post<any>(url, {value}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({success: false, error: 'dwAwakeSet error: ' + e})),
            map((v: any) => v),
        );
    }

    public dwAwakeGet(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/awakeGet`;
        return this._http.get<any>(url, header).pipe(
            catchError((e) => of({success: false, error: 'dwAwakeGet error: ' + e})),
            map((v: any) => v),
        );
    }

    public saveCustomerDynawebsInfo(options: IOrderForm): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSaveCustomerInfo`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem saveCustomerDynawebsInfo post " + e);
            }),
            map((v: any) => v),
        );
    }

    public saveEquipment(options: IOrderForm): Observable<IUserUpgradeReply> {
        let header: object;
        const url = `${this.appSettings.globalService}/saveEquipment`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem saveCustomerInfo post " + e);
            }),
            map((v: any) => {
                return v;
            }),
        );
    }

    public loadEquipment(vehicleId: string): Observable<IUserUpgradeReply> {
        const url = `${this.appSettings.globalService}/loadEquipment/${vehicleId}`;
        return this._http.get<IOrderForm>(url).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem saveCustomerInfo post " + e);
            }),
            map((v: any) => {
                return v;
            }),
        );
    }


    cartDynawebsAddContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCartAddContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            }),
        );
    }

    getDynawebsNewOrder(payerId): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCreateOrder/${payerId}`;
        return this._http.get<ICartReply[]>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => v),
        );
    }

    searchDynawebsServers(options): Observable<IDynawebsServerReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSearchServer`;
        return this._http.put<IDynawebsServerReply>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IDynawebsServerReply) => v),
        );
    }

    saveDynawebsServer(options): Observable<IDynawebsServerReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSaveServer`;
        return this._http.put<IDynawebsServerReply>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IDynawebsServerReply) => v),
        );
    }

    orderStatusDynawebs(status): Observable<IOrderForm[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwOrderStatus/${status}`;
        return this._http.put<any>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IOrderForm[]) => v),
        );
    }

    getDynawebsProducts(): Observable<Array<ICartProduct>> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `${this.appSettings.globalService}/dwCartGetProducts`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: Array<ICartProduct>) => v),
        );
    }

    cartRemoveDynawebsContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCartRemoveContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            }),
        );
    }


    public upgradeAccount(options: object): Observable<IUserSaveDetailsReply> {
        options["customerUser"] = this.m_user.email;
        options["customerPass"] = this.m_user.passwordNew;
        options["version"] = "2.0";


        const url = "https://galaxy.signage.me/Web/upgradeToResellerAccount.ashx";
        this.toast.info(this.translocoService.translate("toast.pleaseWaitUpgradingAccount", {}, this.translocoService.getActiveLang()));
        return this._http.post(url, options).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem upgradeEnterprise post " + e);
            }),
            map((v: any) => {
                return v;
            }),
        );
    }

    public dynawebsUpgradeAccount(options: object): Observable<IUserSaveDetailsReply> {
        options["billingUser"] = this.m_user.email;
        options["billingPass"] = this.m_user.passwordNew;
        options["dynawebs"] = true;
        options["command"] = "upgradeToResellerAccount";
        const url = "https://galaxy.signage.me/Web/upgradeToResellerAccount.ashx";
        return this._http.post(url, options)
            .pipe(
                map((v: any) => v),
            );
    }

    public getBillingService(options: object): Observable<IUserSaveDetailsReply> {
        options["billingUser"] = this.m_user.email;
        options["billingPass"] = this.m_user.passwordNew;
        const url = "https://galaxy.signage.me/Web/billingService.ashx";
        return this._http.post(url, options)
            .pipe(
                map((v: any) => v),
            );
    }

    public getStripeService(options: object): Observable<IUserSaveDetailsReply> {
        options["customerUser"] = this.m_user.email;
        options["customerPass"] = this.m_user.passwordNew;
        const url = "https://galaxy.signage.me/Web/stripeService.ashx";
        return this._http.post(url, options)
            .pipe(
                map((v: any) => v),
            );
    }

    public dwLoadCustomer(options): Observable<IOrderForm> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwLoadCustomer/${options.dwLoad}/${options.orderId}`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwLoadCustomer post " + e);
            }),
            switchMap(v => {
                const obs2$ = this.fintechService.getOrderInfo((<any> v).order_id);
                return combineLatest(of(v), obs2$).pipe(
                    map(([value1, value2]: [IOrderForm, any]) => {
                        if (value2.status == "succeeded") {
                            value1.mail = value2.contact_email;

                            value1.tel = value2.contact_phone1;

                            value1.billingCompany = value2.contact_company;
                            value1.billingFirstName = value2.contact_first_name;
                            value1.billingLastName = value2.contact_last_name;
                            value1.shippingCompany = value2.shipping_company;
                            value1.shippingFirstName = value2.shipping_first_name;
                            value1.shippingLastName = value2.shipping_last_name;
                            value1.shippingAddress1 = value2.shipping_address1;
                            value1.shippingAddress2 = value2.shipping_address2;
                            value1.shippingCity = value2.shipping_city;
                            value1.shippingState = value2.shipping_state;
                            value1.shippingCountry = value2.shipping_country;
                            value1.shippingZip = value2.shipping_zip_code;
                            value1.phone1 = value2.shipping_phone1;
                            value1.phone2 = value2.shipping_phone2;
                            value1.card_number = value2.card_last4;
                            value1.expiration_month = value2.card_expiration_month;
                            value1.expiration_year = value2.card_expiration_year;
                            value1.billingAddress1 = value2.card_address1;
                            value1.billingAddress2 = value2.card_address2;
                            value1.billingCity = value2.card_city;
                            value1.billingState = value2.card_state;
                            value1.billingZip = value2.card_postal_code;
                            value1.billingCountry = value2.card_country;
                        }

                        return value1;
                    }),
                    map((v: any) => {
                        if (v?.error) alert("err: " + v.error);
                        return v;
                    }),
                );
            }),
        );
    }

    public dwDeleteOrder(orderId): Observable<IOrderForm> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwDeleteOrder/${orderId}`;
        return this._http.put<IOrderForm>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwDeleteOrder post " + e);
            }),
            map((v: any) => v),
        );
    }

    // public dwSubscriberGetPayerId(payload): Observable<IOrderForm> {
    //     const httpOptions = {
    //         headers: new HttpHeaders({'Content-Type': 'application/json'}),
    //         responseType: 'json' as 'json'
    //     };
    //     const url = `${this.appSettings.globalService}/msAuthenticateToken_v2`;
    //     return this._http.put<IOrderForm>(url, {token: payload.cartRequest.payerIdToken, version: 'v3'}, httpOptions).pipe(
    //         retryWithBackoff(url, this.storeSelector, this.toast),
    //         catchError((e) => {
    //             this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
    //             return of('problem dwSubscriberGetPayerId post ' + e);
    //         }),
    //         map((v: any) => v)
    //     );
    // }


    public dwGenerateEncryptedId(payload): Observable<IOrderForm> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwGenerateEncryptedId/${payload.businessId}`;
        return this._http.put<IOrderForm>(url, "", header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwDeleteOrder post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwFindOrder(options): Observable<IOrderForm[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwFindOrder`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwFindOrder post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwFindCustomer(options): Observable<IDynawebsCustomer[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwFindCustomer`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwFindCustomer post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwRenameCustomer(options): Observable<IOrderForm[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwRenameUser`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwRenameCustomer post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwCheckUserExists(value): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `${this.appSettings.globalService}/dwCheckUserExists/${value.user}`;
        return this._http.put<any>(url, "", httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwCheckUserExists post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwCartGetContent(options: ICartRequest): Observable<ICart> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCartGetContent/${options.cartCreate.orderId}`;
        return this._http.put<ICart>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwCartGetContent post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwGetChatArchive(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwGetChatArchive/${val}`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwGetChatArchive post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwGetChatArchiveSearch(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwGetChatArchiveSearch/${val}`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwGetChatArchiveSearch post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwReportSubscribers(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwReportSubscribers`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwGetChatArchiveSearch post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwReportPayers(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwReportPayers`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwGetChatArchiveSearch post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwReportNonPayers(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwReportNonPayers`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwGetChatArchiveSearch post " + e);
            }),
            map((v: any) => v),
        );
    }

    public dwGetAccountDetails(businessId: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/getAccountStatusBusiness/${businessId}`;
        return this._http.put<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem dwGetChatArchiveSearch post " + e);
            }),
            map((v: any) => v),
        );
    }

    /**
     * Shopping Cart
     */
    public saveCustomerInfoV4(options: IOrderForm): Observable<IUserUpgradeReply> {
        let header: object;
        if (options.billingToken) {
            header = {
                headers: new HttpHeaders({ "Content-Type": "application/json" }),
                responseType: "json" as "json",
            };
        } else {
            header = getCredentialsHeader(this.m_user);
        }
        const url = `${this.appSettings.globalService}/userSaveDetails`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem saveCustomerInfo post " + e);
            }),
            map((v: any) => {
                return v;
            }),
        );
    }

    public updateCustomerEmail(options: any): Observable<IUserUpgradeReply> {
        const header: object = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/userUpdateEmail`;
        return this._http.put<any>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem updateCustomerEmail post " + e);
            }),
            map((v: any) => {
                return v;
            }),
        );
    }

    getCartContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `${this.appSettings.globalService}/cartGetContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            }),
        );
    }

    cartAddContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `${this.appSettings.globalService}/cartAddContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            }),
        );
    }

    cartPaypalPaymentWaiting(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/cartPaypalPaymentWaiting`;
        return this._http.put<ICartProduct>(url, cartRequest, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            }),
        );
    }

    cartPaypalPayment(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/cartPaypalPayment`;
        return this._http.put<ICartProduct>(url, cartRequest, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            }),
        );
    }

    cartCreditCardPayment(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/cartCreditCardPayment`;
        return this._http.put<ICartProduct>(url, cartRequest, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            }),
        );
    }

    cartRemoveContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `${this.appSettings.globalService}/cartRemoveContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            }),
        );
    }

    cartUpdateShipping(cartRequest: ICartRequest): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `${this.appSettings.globalService}/cartUpdateShipping/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v) => {
                return v;
            }),
        );
    }

    public postChatStats(options: IChatStats): Observable<any> {
        const url = `${this.appSettings.globalService}/chatStats`;
        return this._http.post<IChatStats>(url, options).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate("general.problemNetwork", {}, this.translocoService.getActiveLang()));
                return of("problem chatStats post " + e);
            }),
            map((v: any) => ({ payload: { serverReplay: v } })),
        );
    }

    getCartActiveProducts(): Observable<Array<ICartProduct>> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `${this.appSettings.globalService}/cartGetProducts`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: Array<ICartProduct>) => {
                v = v.map((p) => {
                    if (compareIds(p.product_id, 1122)) p.inAmazon = true;
                    return p;
                });
                return v;
            }),
        );
    }

    getCartCreate(): Observable<ICartCreate> {
        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            responseType: "json" as "json",
        };
        const url = `${this.appSettings.globalService}/createOrder`;
        return this._http.get<ICartCreate>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartCreate) => v),
        );
    }

    initSocketChatService(i_server) {
        this.socketChat = io(i_server, { transports: ["websocket"] });
        return this.socketChat;
    }

    /**
     * Displays save error messages to the user with detailed error information
     *
     * @description
     * Centralized error handler for CRUD save operations. Extracts error message from
     * various error object formats and displays user-friendly toast notification.
     * Skips error display for Galaxy (special deployment mode).
     *
     * @param err - Error object from failed save operation
     * @param crudRecord - CRUD operation description for logging
     *
     * @private
     * @remarks
     * - Extracts message from err.msg, err.message, or err.status
     * - Skips toast for Galaxy deployment mode
     * - Logs to console for debugging
     */
    private showSaveError(err, crudRecord: string) {
        console.error("showSaveError: could not complete the save operation " + crudRecord);
        if (isGalaxy()) return;
        let message = "";
        if (err?.msg) {
            message = err.msg;
        } else if (err?.message) {
            message = err.message;
        } else if (err?.status) {
            message = err.status;
        } else {
            message = err;
        }
        this.toast.error(this.translocoService.translate("toast.sorryEncounteredProblemSave", {}, this.translocoService.getActiveLang()), message);
    }

    // ============================================================================
    // SECTION: Pexels Stock Media Integration
    // ============================================================================

    /**
     * Searches for free stock photos on Pexels and optionally downloads them as resources
     *
     * @description
     * Integrates with Pexels API to search millions of free high-quality stock photos.
     * Results can be previewed or automatically downloaded and added to the user's
     * resource library for use in digital signage scenes.
     *
     * @param query - Search query string (e.g., 'sunset', 'business meeting', 'nature')
     * @param perPage - Number of results per page (default: 20, max: 80)
     * @param page - Page number for pagination (default: 1)
     * @param download - If true, downloads selected images as SignStudio resources (default: false)
     *
     * @returns Observable<any> - Pexels search results with photo metadata
     *
     * @remarks
     * **Search Results Include:**
     * - Photo ID and photographer info
     * - Multiple size variants (original, large, medium, small, thumbnail)
     * - Photo dimensions and aspect ratio
     * - Average color for theming
     * - Direct download URLs
     *
     * **Download Behavior:**
     * - When download=true, images are fetched and saved to user's resource library
     * - Images are optimized for digital signage use
     * - Metadata includes photographer attribution
     *
     * **Pexels License:**
     * - All photos are free to use (Pexels License)
     * - No attribution required but appreciated
     * - Commercial use allowed
     *
     * **Pagination:**
     * - Use perPage and page for browsing large result sets
     * - Maximum 80 results per page
     *
     * @example
     * ```typescript
     * // Search for sunset photos
     * this.userService.pexelsSearchPhotos('sunset', 20, 1, false)
     *     .subscribe(results => {
     *         results.photos.forEach(photo => {
     *             console.log(`${photo.photographer}: ${photo.src.large}`);
     *         });
     *     });
     *
     * // Search and download business images
     * this.userService.pexelsSearchPhotos('business', 10, 1, true)
     *     .subscribe(results => {
     *         console.log(`Downloaded ${results.downloaded} images to resources`);
     *     });
     * ```
     */
    public pexelsSearchPhotos(query: string, perPage: number = 20, page: number = 1, download: boolean = false): Observable<any> {
        if (isNullOrUndefOrEmpty(this.m_user))
            return EMPTY;
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/pexels/searchPhotos`;
        const body = {
            query: query,
            perPage: perPage,
            page: page,
            download: download,
        };
        return this._http.put<any>(url, body, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ error: "problem pexelsSearchPhotos: " + e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Searches for free stock videos on Pexels and optionally downloads them as resources
     *
     * @description
     * Integrates with Pexels API to search thousands of free high-quality stock videos.
     * Videos are available in multiple resolutions and can be automatically downloaded
     * and added to the user's resource library for use in digital signage content.
     *
     * @param query - Search query string (e.g., 'ocean waves', 'city traffic', 'nature timelapse')
     * @param perPage - Number of results per page (default: 20, max: 80)
     * @param page - Page number for pagination (default: 1)
     * @param download - If true, downloads selected videos as SignStudio resources (default: false)
     * @param quality - Preferred video quality (default: 'hd')
     *
     * @returns Observable<any> - Pexels video search results with metadata
     *
     * @remarks
     * **Video Quality Options:**
     * - **'uhd'**: Ultra HD 4K (3840×2160 or higher)
     * - **'hd'**: Full HD 1080p (1920×1080) - Default, best for most signage
     * - **'sd'**: Standard definition (720p or lower)
     *
     * **Search Results Include:**
     * - Video ID and videographer info
     * - Multiple quality variants (uhd, hd, sd)
     * - Video dimensions, duration, and fps
     * - Preview image/thumbnail
     * - Direct download URLs for each quality
     *
     * **Download Behavior:**
     * - When download=true, videos are fetched in specified quality
     * - Saved to user's resource library with metadata
     * - Includes videographer attribution
     * - Optimized for digital signage playback
     *
     * **Pexels License:**
     * - All videos are free to use (Pexels License)
     * - No attribution required but appreciated
     * - Commercial use allowed
     * - Can be edited and modified
     *
     * **File Size Considerations:**
     * - UHD videos can be very large (100MB+)
     * - HD videos are typically 10-50MB
     * - SD videos are smaller for bandwidth-limited scenarios
     *
     * @example
     * ```typescript
     * // Search for HD nature videos
     * this.userService.pexelsSearchVideos('nature', 15, 1, false, 'hd')
     *     .subscribe(results => {
     *         results.videos.forEach(video => {
     *             console.log(`${video.user.name}: ${video.duration}s`);
     *             console.log(`HD URL: ${video.video_files.find(f => f.quality === 'hd')?.link}`);
     *         });
     *     });
     *
     * // Search and download 4K city videos
     * this.userService.pexelsSearchVideos('city skyline', 5, 1, true, 'uhd')
     *     .subscribe(results => {
     *         console.log(`Downloaded ${results.downloaded} 4K videos to resources`);
     *     });
     *
     * // Get SD videos for lower bandwidth
     * this.userService.pexelsSearchVideos('abstract', 10, 1, false, 'sd')
     *     .subscribe(results => {
     *         console.log('SD videos for quick preview');
     *     });
     * ```
     */
    public pexelsSearchVideos(query: string, perPage: number = 20, page: number = 1, download: boolean = false, quality: string = "hd"): Observable<any> {
        if (isNullOrUndefOrEmpty(this.m_user))
            return EMPTY;
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/pexels/searchVideos`;
        const body = {
            query: query,
            perPage: perPage,
            page: page,
            download: download,
            quality: quality,
        };
        return this._http.put<any>(url, body, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ error: "problem pexelsSearchVideos: " + e });
            }),
            map((v: any) => v),
        );
    }

    /**
     * Generate a SignStudio scene from a natural language prompt using AI
     *
     * @description
     * Sends a natural language prompt to the AI scene generator endpoint which uses Claude AI
     * to convert the prompt into a fully functional SignStudio scene JSON structure.
     *
     * The AI is trained on comprehensive scene architecture documentation and can generate:
     * - Simple text-based scenes (dashboards, menus, schedules)
     * - Complex layouts with icons and styling
     * - Scenes with stock images from Pexels (automatic search and integration)
     *
     * @endpoint POST /createScenes
     * @controller C:\msweb\mars\controllers\aiServices.js
     * @aiService C:\msweb\mars\ai\sceneGeneratorAI.js
     *
     * @param {string} prompt - Natural language description of the scene to create
     * @returns {Observable<any>} Scene generation response with scene JSON and optional stock images
     *
     * @example
     * this.userService.generateAIScene("create a weather dashboard with temperature")
     *   .subscribe(response => {
     *     if (response.success) {
     *       console.log('Generated scene:', response.scene);
     *       console.log('Suggested images:', response.suggestedImages);
     *     }
     *   });
     */
    generateAIScene(prompt: string, engine: string = 'claude'): Observable<any> {
        const url = `${this.appSettings.globalService}/createScenes`;
        const header = getCredentialsHeader(this.m_user);
        const body = { prompt, engine };
        return this._http.post<any>(url, body, header).pipe(
            catchError((error) => {
                console.error('[AI Scene Generation] Error:', error);

                // Handle different error types gracefully
                let errorMessage = "Could not generate scene. Your prompt may need more details or the AI service may be temporarily unavailable.";

                if (error.status === 400) {
                    errorMessage = "Could not generate scene. Please try rephrasing your prompt with more specific details.";
                } else if (error.status === 500) {
                    errorMessage = "AI service error. Please try again in a moment.";
                } else if (error.status === 0 || error.name === 'HttpErrorResponse') {
                    errorMessage = "Network error. Please check your connection and try again.";
                } else if (error.message) {
                    errorMessage = error.message;
                }

                return of({
                    success: false,
                    error: errorMessage,
                    status: error.status || 0
                });
            }),
            map((response: any) => {
                // Validate response has scene data
                if (response && response.success && !response.scene) {
                    console.warn('[AI Scene Generation] Success response missing scene data:', response);
                    return {
                        success: false,
                        error: "AI generated an invalid response. Please try again with a different prompt."
                    };
                }
                return response;
            }),
        );
    }

    /**
     * Proxy ICMP ping through mars. Browser → /pingStation → server runs
     * `ping -c 1` against the IP → returns latency. Used by the Network
     * Builder's per-station live ping graph. Network errors flatten to
     * a success:false result so the polling caller stays alive.
     */
    pingStation(ip: string, stationBusinessId: string): Observable<any> {
        const url = `${this.appSettings.globalService}/pingStation`;
        const header = getCredentialsHeader(this.m_user);
        const body = {ip};
        return this._http.post<any>(url, body, header).pipe(
            catchError((e) => of({success: false, ip, ms: null, t: Date.now(), error: e?.message || "network"})),
            map((v: any) => ({...v, stationBusinessId})),
        );
    }

    /**
     * Refine an existing AI-generated scene with a follow-up instruction
     * @param scene Current scene JSON
     * @param refinement Natural language refinement instruction
     * @param conversationHistory Previous refinement history for context
     */
    refineAIScene(scene: any, refinement: string, conversationHistory: Array<{ role: string; content: string }>): Observable<any> {
        const url = `${this.appSettings.globalService}/refineScene`;
        const header = getCredentialsHeader(this.m_user);
        const body = { scene, refinement, conversationHistory };
        return this._http.post<any>(url, body, header).pipe(
            catchError((error) => {
                console.error('[AI Scene Refinement] Error:', error);
                let errorMessage = "Could not refine scene. Please try a different instruction.";
                if (error.status === 400) {
                    errorMessage = "Invalid refinement request. Please try rephrasing.";
                } else if (error.status === 500) {
                    errorMessage = "AI service error. Please try again in a moment.";
                } else if (error.status === 0 || error.name === 'HttpErrorResponse') {
                    errorMessage = "Network error. Please check your connection.";
                }
                return of({ success: false, error: errorMessage, status: error.status || 0 });
            }),
            map((response: any) => {
                if (response && response.success && !response.scene) {
                    return { success: false, error: "AI refinement returned an invalid response." };
                }
                return response;
            }),
        );
    }

    /**
     * Generate multiple scene variations from a single prompt
     * @param prompt Natural language description
     * @param variationCount Number of variations to generate (2-3)
     */
    generateAISceneVariations(prompt: string, variationCount: number = 3): Observable<any> {
        const url = `${this.appSettings.globalService}/createSceneVariations`;
        const header = getCredentialsHeader(this.m_user);
        const body = { prompt, variationCount };
        return this._http.post<any>(url, body, header).pipe(
            catchError((error) => {
                console.error('[AI Scene Variations] Error:', error);
                let errorMessage = "Could not generate variations. Please try again.";
                if (error.status === 429) {
                    errorMessage = "Rate limit exceeded. Please wait before generating variations.";
                } else if (error.status === 0 || error.name === 'HttpErrorResponse') {
                    errorMessage = "Network error. Please check your connection.";
                }
                return of({ success: false, error: errorMessage, status: error.status || 0 });
            }),
            map((response: any) => {
                if (response && response.success && (!response.variations || response.variations.length === 0)) {
                    return { success: false, error: "AI variations returned an invalid response. Please try again." };
                }
                return response;
            }),
        );
    }

    /**
     * Generate a Fabric.js paint design from a prompt
     * @param prompt Natural language description of the design
     * @param width Canvas width (default 500)
     * @param height Canvas height (default 500)
     */
    generateAIPaint(prompt: string, width: number = 500, height: number = 500): Observable<any> {
        const url = `${this.appSettings.globalService}/createPaint`;
        const header = getCredentialsHeader(this.m_user);
        const body = { prompt, width, height };
        return this._http.post<any>(url, body, header).pipe(
            catchError((error) => {
                console.error('[AI Paint] Error:', error);
                let errorMessage = "Could not generate paint. Please try again.";
                if (error.status === 429) {
                    errorMessage = "Rate limit exceeded. Please wait before generating.";
                } else if (error.status === 0 || error.name === 'HttpErrorResponse') {
                    errorMessage = "Network error. Please check your connection.";
                }
                return of({ success: false, error: errorMessage });
            }),
            map((response: any) => {
                if (response && response.success && !response.paintData) {
                    return { success: false, error: "AI returned invalid paint data." };
                }
                return response;
            }),
        );
    }

    // ========================================
    // TICKET SYSTEM METHODS
    // ========================================

    /**
     * Create a new support ticket (no auth required - customers may not be logged in)
     * @param payload Ticket creation data
     */
    createTicket(payload: ICreateTicketRequest): Observable<ICreateTicketResponse> {
        const url = `${this.appSettings.globalService}/createTicket`;
        const body = { ...payload, is_dev: isHostDevOrLocal() };
        return this._http.post<ICreateTicketResponse>(url, body).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error creating ticket: " + e.message });
            }),
            map((v: ICreateTicketResponse) => v),
        );
    }

    /**
     * Get list of tickets with optional filters
     * @param payload Filter options
     */
    getTickets(payload: IGetTicketsFilter): Observable<IGetTicketsResponse> {
        const url = `${this.appSettings.globalService}/getTickets`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<IGetTicketsResponse>(url, payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error fetching tickets: " + e.message });
            }),
            map((v: IGetTicketsResponse) => v),
        );
    }

    /**
     * Get a single ticket with all messages
     * @param ticketId Ticket ID
     */
    getTicket(ticketId: number): Observable<IGetTicketResponse> {
        const url = `${this.appSettings.globalService}/getTicket`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<IGetTicketResponse>(url, { ticket_id: ticketId }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error fetching ticket: " + e.message });
            }),
            map((v: IGetTicketResponse) => v),
        );
    }

    /**
     * Update ticket status, priority, or assignment
     * @param payload Update data
     */
    updateTicket(payload: IUpdateTicketRequest): Observable<IUpdateTicketResponse> {
        const url = `${this.appSettings.globalService}/updateTicket`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<IUpdateTicketResponse>(url, payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error updating ticket: " + e.message });
            }),
            map((v: IUpdateTicketResponse) => v),
        );
    }

    /**
     * Add a message or reply to a ticket
     * @param payload Message data
     */
    addTicketMessage(payload: IAddTicketMessageRequest): Observable<IAddTicketMessageResponse> {
        const url = `${this.appSettings.globalService}/addTicketMessage`;
        const header = getCredentialsHeader(this.m_user);
        const body = { ...payload, is_dev: isHostDevOrLocal() };
        return this._http.post<IAddTicketMessageResponse>(url, body, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error adding message: " + e.message });
            }),
            map((v: IAddTicketMessageResponse) => v),
        );
    }

    /**
     * Close a ticket with optional resolution notes
     * @param payload Close ticket data
     */
    closeTicket(payload: ICloseTicketRequest): Observable<ICloseTicketResponse> {
        const url = `${this.appSettings.globalService}/closeTicket`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<ICloseTicketResponse>(url, payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error closing ticket: " + e.message });
            }),
            map((v: ICloseTicketResponse) => v),
        );
    }

    /**
     * Get ticket statistics for dashboard
     */
    getTicketStats(): Observable<IGetTicketStatsResponse> {
        const url = `${this.appSettings.globalService}/getTicketStats`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<IGetTicketStatsResponse>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error fetching stats: " + e.message });
            }),
            map((v: IGetTicketStatsResponse) => v),
        );
    }

    /**
     * Get customer ticket by encrypted hash (no auth required)
     */
    getCustomerTicket(payload: IGetCustomerTicketRequest): Observable<IGetCustomerTicketResponse> {
        const url = `${this.appSettings.globalService}/getCustomerTicket`;
        return this._http.post<IGetCustomerTicketResponse>(url, payload).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error: " + (e.message || e) });
            }),
            map((v: IGetCustomerTicketResponse) => v),
        );
    }

    /**
     * Add customer reply to ticket (no auth required)
     */
    addCustomerTicketMessage(payload: IAddCustomerTicketMessageRequest): Observable<IAddCustomerTicketMessageResponse> {
        const url = `${this.appSettings.globalService}/addCustomerTicketMessage`;
        return this._http.post<IAddCustomerTicketMessageResponse>(url, payload).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error: " + (e.message || e) });
            }),
            map((v: IAddCustomerTicketMessageResponse) => v),
        );
    }

    /**
     * Get all canned responses
     */
    getCannedResponses(category?: string): Observable<IGetCannedResponsesResponse> {
        const url = `${this.appSettings.globalService}/getCannedResponses`;
        const header = getCredentialsHeader(this.m_user);
        const body = category ? { category } : {};
        return this._http.post<IGetCannedResponsesResponse>(url, body, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error: " + (e.message || e) });
            }),
            map((v: IGetCannedResponsesResponse) => v),
        );
    }

    /**
     * Add a new canned response
     */
    addCannedResponse(payload: IAddCannedResponseRequest): Observable<IAddCannedResponseResponse> {
        const url = `${this.appSettings.globalService}/addCannedResponse`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<IAddCannedResponseResponse>(url, payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error: " + (e.message || e) });
            }),
            map((v: IAddCannedResponseResponse) => v),
        );
    }

    /**
     * Update an existing canned response
     */
    updateCannedResponse(payload: IUpdateCannedResponseRequest): Observable<IUpdateCannedResponseResponse> {
        const url = `${this.appSettings.globalService}/updateCannedResponse`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<IUpdateCannedResponseResponse>(url, payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error: " + (e.message || e) });
            }),
            map((v: IUpdateCannedResponseResponse) => v),
        );
    }

    /**
     * Delete a canned response
     */
    deleteCannedResponse(payload: IDeleteCannedResponseRequest): Observable<IDeleteCannedResponseResponse> {
        const url = `${this.appSettings.globalService}/deleteCannedResponse`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<IDeleteCannedResponseResponse>(url, payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of({ success: false, error: "Error: " + (e.message || e) });
            }),
            map((v: IDeleteCannedResponseResponse) => v),
        );
    }

    // ============================================================================
    // SECTION: Monitor Wall v2 — mars /monitorWall/* (roadmap M2)
    // ============================================================================

    monitorMintToken(studioId: string): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/mintToken`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, { studioId }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorMintToken: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorRedeemToken(token: string): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/redeemToken`;
        const header = { headers: new HttpHeaders({ "Content-Type": "application/json" }), responseType: "json" as "json" };
        return this._http.post<any>(url, { token }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorRedeemToken: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorPostEvents(events: any[]): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/postEvents`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, { events }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorPostEvents: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorRefreshIdentities(items: Array<{stationBusinessId: string; stationName: string; stationId: string}>): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/refreshIdentities`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, { items }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorRefreshIdentities: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorGetFeed(sinceMs?: number, limit?: number): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/getFeed`;
        const header = getCredentialsHeader(this.m_user);
        const body: any = {};
        if (sinceMs) body.sinceMs = sinceMs;
        if (limit) body.limit = limit;
        return this._http.post<any>(url, body, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorGetFeed: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorGetHistory(stationBusinessId: string, hours: number): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/getHistory`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, { stationBusinessId, hours }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorGetHistory: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorAck(eventId: string): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/ack`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, { eventId }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorAck: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorStreamTicket(): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/streamTicket`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorStreamTicket: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorGetConfig(): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/getConfig`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorGetConfig: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorPutConfig(config: any): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/putConfig`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, { config }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorPutConfig: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorGetStates(): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorWall/getStates`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorGetStates: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    /**
     * MonitorService (mars cron) — 4 endpoints for background-monitoring config.
     * See C:\msweb\mars\controllers\monitorServiceApi.js.
     */
    monitorServiceGetConfig(): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorService/getBusinessConfig`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorServiceGetConfig: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorServiceSetConfig(body: { enabled?: boolean, pollMinutes?: number }): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorService/setBusinessConfig`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, body, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorServiceSetConfig: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorServiceAddStation(stationBusinessId: string): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorService/addStation`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, { stationBusinessId }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorServiceAddStation: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }

    monitorServiceRemoveStation(stationBusinessId: string): Observable<any> {
        const url = `${this.appSettings.globalService}/monitorService/removeStation`;
        const header = getCredentialsHeader(this.m_user);
        return this._http.post<any>(url, { stationBusinessId }, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => of({ error: "monitorServiceRemoveStation: " + (e?.message || e) })),
            map((v: any) => v),
        );
    }
}

